import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  Suspense,
} from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
// The Restaurant Orders tab strip's own list, so the global search can offer
// those pages without a second copy of them living here.
import { allowedRestaurantTabs } from './components/RestaurantOrdersLayout';
// ...and the other three tab strips, for the same reason.
import { allowedRestaurantSetupTabs } from './components/RestaurantMastersLayout';
import { allowedRestroReportTabs } from './components/RestroReportsLayout';
import { allowedPubCardTabs } from './components/PubCardLayout';
import config from '../config';
import { setDescFlagsCache } from './components/descFlags';
// The outline shown in the content area while a screen's chunk arrives.
import RouteSkeleton from './components/RouteSkeleton';
import { notifyRbacUpdated } from './rbac';
import { getBrand } from './brand';
import { subscriptionApi, getCompanyId } from './components/subscriptionApi';
import { openNotifyStream } from './components/notifyStream';
import { useNotificationSound } from './components/notifySound';
// POS full screen: the till hides this layout's bar and sidebar.
import { usePosFullscreen } from './components/posFullscreen';
import { toast } from 'react-toastify';
// Page-wise "Open …" switches (Users → User Permissions). Applied on top of
// the menuKey grant below; fail-open for a user with no permission codes.
import { PAGE_RBAC } from './rbac/permissionCodes';
import { hasStoredPermission } from './rbac/useRequirePerm';
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Collapse,
  Button,
  useTheme,
  useMediaQuery,
  alpha,
  Autocomplete,
  TextField,
  Popover,
  Badge,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Hotel as HotelIcon,
  Home as HomeSharp,
  MeetingRoom as MeetingRoomIcon,
  AddBox as AddBoxIcon,
  CreditCard as CreditCardIcon,
  Inventory as InventoryIcon,
  ChevronLeft as ChevronLeftIcon,
  ExitToApp as ExitToAppIcon,
  ExpandLess,
  ExpandMore,
  Work as WorkIcon,
  Settings as SettingsIcon,
  Description as DescriptionIcon,
  MonetizationOn as MonetizationOnIcon,
  KeyboardArrowRight as ArrowIcon,
  Lock as LockIcon,
  ArrowCircleUp as UpgradeIcon,
  ArrowBack as ArrowBackIcon,
  Restaurant as RestaurantIcon,
  RoomService as RoomServiceIcon,
  Payments as PaymentsIcon,
  Groups as GroupsIcon,
  Assessment as AssessmentIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  SupportAgent as SupportAgentIcon,
  LinkOutlined as LinkIcon,
} from '@mui/icons-material';

const drawerWidthExpanded = 280;
const drawerWidthCollapsed = 68;

// NOTE: company / Users / userName / isRestaurantOnly / restaurantEnabled /
// hotelEnabled and the brand + user-display values are computed INSIDE the
// AdminLayout component so they read a FRESH sessionStorage on every render.
// Evaluating them here (module load = login page, before companyDetails exists)
// would permanently cache isRestaurantOnly=false and misroute restaurant logins.

// Whole top-level groups that belong to one domain — hidden when that domain
// isn't enabled, so no Restaurant/Hotel module (or name) shows anywhere.
const RESTAURANT_MENUS = new Set([
  // top-level department
  'Restaurant',
  // children living inside mixed departments
  'Restaurant Dashboard',
  'Restaurant Setup',
  'QR Codes',
  // Swiggy / Zomato sell restaurant food; a hotel with no restaurant has
  // nothing to list on them.
  'Online Ordering',
  // The four restaurant reports were merged into one entry; this is the label
  // the sidebar now renders. The old names are gone from the menu, so listing
  // them here would match nothing.
  'Restro Report',
  'Item Report',
  'Order History',
  // Restored 2026-09-17. Both work on restaurant orders or items: dues come
  // from orders settled as Due, the old purchase screen is keyed on
  // restaurant items.
  'Customer Due Report',
  'Purchase Entry',
]);
const HOTEL_MENUS = new Set([
  // ── top-level departments ──
  'Reception',
  // Accommodation (dormitory/bed) is a rooms-domain feature — a restaurant-only
  // property has no beds to manage.
  'Accommodation',
  'Dormitories',
  'Bed Map',
  // Every child of Services is a guest-room amenity (spa, laundry, mini-bar,
  // housekeeping). A restaurant-only property has no rooms, so the whole
  // department goes — that also covers the Housekeeping rows below.
  'Services',
  // hotel landing — hidden for restaurant-only so only the Restaurant Dashboard shows
  'Hotel Dashboard',

  // ── Setup ──
  'My Hotel',
  'Rooms',
  'Room Types',
  'Packages',
  'Extras',
  // Travel agents book rooms, not tables.
  'Agents',

  // ── Services (listed individually as well, so a future move out of the
  //    Services department cannot silently un-hide them) ──
  'Spa Bookings',
  'Spa Services',
  'Spa Therapists',
  'Laundry Orders',
  'Laundry Items',
  'Mini-Bar Consumption',
  'Mini-Bar Items',
  'Housekeeping Dashboard',
  'Housekeeping Tasks',
  'Housekeeping Staff',

  // ── Reports that report on rooms, stays or room guests ──
  'Booking Report',
  'Guest Report',
  'Night Audit',
  'Occupancy Report',
  'Reserve Event Space',
  'Banquet Report',
  'Banquet Calendar',
  'Settlement Report',
  'Sale Report (Per Day)',
  'Hold / Maintenance Rooms',
  'Package Report',
  'Add-On Amount',
  'Room List',
  // Room-stay invoices. The restaurant has its own separate
  // "Restaurant Invoice Report", which stays visible.
  'Invoice Report',
  // History of a hotel BOOKING (rooms, stay dates, room charges) — it sat in
  // RESTAURANT_MENUS by mistake, so restaurant-only properties could open a
  // booking lookup for bookings they can never have.
  'Booking History',
  'Agent Report',
  'Agent Commission',
  // Restored 2026-09-17: one booking's ledger and the older travel-agent
  // commission screen - rooms and room guests only.
  'Booking Ledger',
  'Agent Commission (Old)',
]);

// The Restaurant Orders workspace (every tab of the POS: Dine In, Room Service,
// Table Reservation, QR Orders, Takeaway, Pickup, Delivery, Quick Bill, Tables,
// Payments, Order History) runs with the sidebar COLLAPSED so the order screen
// gets the full width. Moving between these tabs must never reopen it — only
// the user opening it themselves does, and that choice then sticks until they
// leave the workspace entirely.
//
// EVERY tab in RestaurantOrdersLayout.js belongs here. One missing makes the
// sidebar spring open halfway along the strip, which reads as the page having
// jumped - Reservations and QrOrders were both missed when they joined it.
const RESTAURANT_ORDERS_PATHS = [
  '/admin/CreateOrder',
  '/admin/Order',
  '/admin/Reservations',
  '/admin/QrOrders',
  '/admin/QuickBill',
  '/admin/TableOrders',
  '/admin/RestaurantPayments',
  '/admin/OrderHistory',
];
const inRestaurantOrders = (path) =>
  RESTAURANT_ORDERS_PATHS.some((p) => path === p || path.startsWith(p + '/'));

/* ── Sidebar palette ── */
const S = {
  // Premium luxury navy — subtle depth, not flat, not glossy.
  bg: '#1A2238',
  bgHover: 'rgba(255,255,255,0.05)',
  bgActive: 'rgba(255,255,255,0.07)',
  border: 'rgba(255,255,255,0.08)',
  textDim: '#B4BECE',
  textHover: '#FFFFFF',
  textActive: '#FFFFFF',
  accent: '#C8102E',
  accentL: '#E23A4E',
  cardShadow: '0 6px 18px rgba(0,0,0,0.30)',
};

// Icon registry for DB-driven menus — MenuMaster.Icon stores an icon NAME string,
// mapped here to a component. Unknown/empty names fall back to a generic icon.
const MENU_ICONS = {
  Dashboard: <DashboardIcon />,
  Hotel: <HotelIcon />,
  Home: <HomeSharp />,
  MeetingRoom: <MeetingRoomIcon />,
  AddBox: <AddBoxIcon />,
  Inventory: <InventoryIcon />,
  Work: <WorkIcon />,
  Settings: <SettingsIcon />,
  Description: <DescriptionIcon />,
  MonetizationOn: <MonetizationOnIcon />,
};
const iconFor = (name) => MENU_ICONS[name] || <DashboardIcon />;

// Sidebar department icons — Google Material Symbols Rounded (font glyphs).
// Inherit ListItemIcon color (active/inactive) via currentColor; sized 22px.
const MSym = ({ name }) => (
  <span
    className='material-symbols-rounded'
    style={{
      fontSize: 22,
      lineHeight: 1,
      color: 'inherit',
      fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24",
    }}>
    {name}
  </span>
);

const fullMenuStructure = [
  // ══ MY DAY — dashboards, the first screen for every role ══
  // Dashboards as flat top-level items (Home group dissolved). "All
  // Properties" / Multi-Hotel is intentionally not listed here.
  // No alwaysShow on either dashboard.
  //
  // They used to carry it, on the reasoning that the dashboard is everybody's
  // landing screen and a missing grant should never bounce someone to
  // /admin/welcome. But it also meant the grant was decoration: a waiter with
  // no dashboard permission was shown the day's takings anyway, and the ticks
  // in User Access did not describe what that person could actually see.
  //
  // The rule now holds everywhere: a screen appears when the person has been
  // given it, and not otherwise.
  {
    text: 'Hotel Dashboard',
    icon: <MSym name='space_dashboard' />,
    path: '/admin/dashboard',
    menuKey: 'dashboard',
  },
  {
    text: 'Restaurant Dashboard',
    icon: <DashboardIcon />,
    path: '/admin/resturantdashbord',
    menuKey: 'resturantdashbord',
  },
  {
    // Ask-AI insights: type any business question ("aaj ki occupancy", "aaj
    // konse room zyada bike", "is mahine ka revenue") and get the exact figures
    // back. Reuses the dashboard grant so it appears for whoever can see the
    // day's numbers (a brand-new key would be hidden for everyone).
    text: 'Ask AI',
    icon: <MSym name='auto_awesome' />,
    path: '/admin/AiInsights',
    menuKey: 'dashboard',
  },
  // ══ FRONT OFFICE — the receptionist's whole day (hotel domain) ══
  {
    text: 'Reception',
    icon: <MSym name='room_service' />,
    children: [
      {
        // Reception's landing screen: today's arrivals, departures, in-house,
        // room readiness and outstanding, each one a link into the real work.
        // Composed entirely from endpoints that already existed.
        text: 'Today',
        icon: <MSym name='today' />,
        path: '/admin/ReceptionToday',
        menuKey: 'BookingReport',
      },
      {
        text: 'New Booking',
        icon: <MeetingRoomIcon />,
        path: '/admin/manageinventory',
        menuKey: 'manageinventory',
      },
      {
        // One-page booking that holds room TYPES (room assigned at check-in).
        // Reuses the New Booking grant so it appears without a new menu key.
        text: 'Fast Booking',
        icon: <MSym name='bolt' />,
        path: '/admin/FastBooking',
        menuKey: 'manageinventory',
      },
      {
        text: 'Quick Booking',
        icon: <MeetingRoomIcon />,
        path: '/admin/QuickBooking',
        menuKey: 'QuickBooking',
      },
      {
        text: 'Room Reservation',
        icon: <MeetingRoomIcon />,
        path: '/admin/RoomReservation',
        menuKey: 'QuickBooking',
      },
      {
        // The two lists the front desk works from every morning. They read the
        // same bookings the grid does - no separate source of truth.
        text: 'Arrivals',
        icon: <MSym name='login' />,
        path: '/admin/Arrivals',
        menuKey: 'BookingReport',
      },
      {
        text: 'Departures',
        icon: <MSym name='logout' />,
        path: '/admin/Departures',
        menuKey: 'BookingReport',
      },
      {
        // All bookings that came in through an OTA (channel manager). Same
        // bookings the Calendar highlights in amber — this is the flat list.
        text: 'OTA Bookings',
        icon: <MSym name='public' />,
        path: '/admin/OtaBookings',
        menuKey: 'BookingReport',
      },
      {
        // Room-first view for reception. Occupancy comes from HotelBookings -
        // the same source as the bookings grid - not from the housekeeping
        // layout, which only sees website bookings.
        text: 'Rooms',
        icon: <MSym name='grid_view' />,
        path: '/admin/RoomGrid',
        menuKey: 'BookingReport',
      },
      {
        // One profile per guest: stays, spend, outstanding and ID. Keyed on
        // mobile because there is no guest master - the API flags when that key
        // has grouped more than one person.
        text: 'Guests',
        icon: <MSym name='person_search' />,
        path: '/admin/GuestProfile',
        menuKey: 'Guestreport',
      },
      {
        text: 'Hourly Booking',
        icon: <MeetingRoomIcon />,
        path: '/admin/HourlyBooking',
        menuKey: 'HourlyBooking',
      },
      {
        text: 'Bookings',
        icon: <DescriptionIcon />,
        path: '/admin/BookingReport',
        menuKey: 'BookingReport',
      },
      {
        text: 'Calendar',
        icon: <DashboardIcon />,
        path: '/admin/Calender',
        menuKey: 'Calender',
      },
      {
        text: 'Change Room',
        icon: <MeetingRoomIcon />,
        path: '/admin/Changeroom',
        menuKey: 'Changeroom',
      },
      {
        text: 'Block Room',
        icon: <MeetingRoomIcon />,
        path: '/admin/HoldRoom',
        menuKey: 'HoldRoom',
      },
      {
        text: 'Group Booking',
        icon: <MeetingRoomIcon />,
        path: '/admin/BulkBookings',
        menuKey: 'BulkBookings',
      },
      {
        text: 'Channel Manager',
        icon: <SettingsIcon />,
        path: '/admin/ChannelManager',
        menuKey: 'hotels',
      },
      {
        text: 'Reset Data',
        icon: <SettingsIcon />,
        path: '/admin/ResetData',
        menuKey: 'hotels',
      },
    ],
  },
  // ══ ACCOMMODATION — dormitory/bed operational visibility. A separate NAV
  //    layer only; booking / check-in / billing / housekeeping stay in the
  //    existing shared PMS modules (no duplicate dormitory system). ══
  {
    text: 'Accommodation',
    icon: <MSym name='night_shelter' />,
    children: [
      {
        // Dormitory master + per-bed configuration (create dorm, add beds,
        // set bed status). Grant menuKey 'Dormitories' in RBAC to show it.
        text: 'Dormitories',
        icon: <MSym name='king_bed' />,
        path: '/admin/Dormitories',
        menuKey: 'Dormitories',
      },
      {
        // Floor/dorm-wise bed grid — Available / Occupied / Reserved / Dirty /
        // Cleaning / Out of Order, with per-bed status change. Grant menuKey
        // 'BedMap' in RBAC to show it.
        text: 'Bed Map',
        icon: <MSym name='grid_view' />,
        path: '/admin/BedMap',
        menuKey: 'BedMap',
      },
      // ── Banquet (halls, packages, event booking). menuKey reuses
      //    'BookingReport' so they inherit an existing grant and aren't hidden.
      {
        text: 'Banquet Hall',
        icon: <MSym name='celebration' />,
        path: '/admin/banquethall/add',
        menuKey: 'BookingReport',
      },
      {
        text: 'Banquet Package',
        icon: <MSym name='restaurant_menu' />,
        path: '/admin/banquethall/package',
        menuKey: 'BookingReport',
      },
      {
        text: 'Reserve Event Space',
        icon: <MSym name='event_seat' />,
        path: '/admin/BanquetReserve',
        menuKey: 'BookingReport',
      },
    ],
  },
  // ══ RESTAURANT — POS only; setup lives in Masters, reports in Reports ══
  {
    text: 'Restaurant',
    icon: <MSym name='restaurant' />,
    children: [
      {
        text: 'Orders',
        icon: <AddBoxIcon />,
        path: '/admin/CreateOrder',
        menuKey: 'CreateOrder',
      },
      // Reservations is NOT here any more. It lives in the Restaurant Orders
      // tab strip as "Table Reservation", beside Room Service - booking a
      // table is part of working the floor, not a separate department. The
      // menuKey 'Reservations' still gates it there, so permissions are
      // unchanged.
      {
        text: 'Kitchen',
        icon: <DashboardIcon />,
        path: '/admin/KitchenPendingOrders',
        menuKey: 'KitchenPendingOrders',
      },
      {
        text: 'KOT Report',
        icon: <DescriptionIcon />,
        path: '/admin/KotReport',
        menuKey: 'KotReport',
      },
    ],
  },
  // ══ PUB / CLUB — the prepaid card desk, its own department ══
  //
  // Deliberately NOT under Restaurant. A pub runs a card float with its own
  // money, its own ledger and its own staff; folding it into the restaurant
  // menu would put a cash-handling department behind a heading about food.
  {
    text: 'Pub / Club',
    icon: <CreditCardIcon />,
    children: [
      {
        text: 'Card Requests',
        icon: <CreditCardIcon />,
        path: '/admin/PubCardRequests',
        menuKey: 'PubCardRequest',
      },
    ],
  },
  // ══ GUEST SERVICES — Spa, Laundry, Mini-Bar, Housekeeping under one roof ══
  {
    text: 'Services',
    icon: <MSym name='concierge' />,
    children: [
      {
        text: 'Spa Bookings',
        icon: <MeetingRoomIcon />,
        path: '/admin/Spa/Bookings',
        menuKey: 'SpaBookings',
      },
      {
        text: 'Spa Services',
        icon: <SettingsIcon />,
        path: '/admin/Spa/Services',
        menuKey: 'SpaServices',
      },
      {
        text: 'Spa Therapists',
        icon: <WorkIcon />,
        path: '/admin/Spa/Therapists',
        menuKey: 'SpaTherapists',
      },
      {
        text: 'Laundry Orders',
        icon: <DescriptionIcon />,
        path: '/admin/Laundry/Orders',
        menuKey: 'LaundryOrders',
      },
      {
        text: 'Laundry Items',
        icon: <SettingsIcon />,
        path: '/admin/Laundry/Items',
        menuKey: 'LaundryItems',
      },
      {
        text: 'Mini-Bar Consumption',
        icon: <DescriptionIcon />,
        path: '/admin/MiniBar/Consumption',
        menuKey: 'MiniBarConsumption',
      },
      {
        text: 'Mini-Bar Items',
        icon: <SettingsIcon />,
        path: '/admin/MiniBar/Items',
        menuKey: 'MiniBarItems',
      },
      {
        text: 'Housekeeping Dashboard',
        icon: <DashboardIcon />,
        path: '/admin/Housekeeping/Dashboard',
        menuKey: 'HousekeepingDashboard',
      },
      {
        text: 'Housekeeping Tasks',
        icon: <DescriptionIcon />,
        path: '/admin/Housekeeping/Tasks',
        menuKey: 'HousekeepingTasks',
      },
      {
        text: 'Housekeeping Staff',
        icon: <WorkIcon />,
        path: '/admin/Housekeeping/Staff',
        menuKey: 'HousekeepingStaff',
      },
    ],
  },
  // ══ INVENTORY & PURCHASE — the store manager's world ══
  {
    text: 'Store',
    icon: <MSym name='inventory_2' />,
    children: [
      {
        // Root of the Store workflow — the item master. Previously only reachable
        // through the Restaurant-Masters tab strip / direct URL; now a first-class
        // Store entry so items are created once and reused everywhere.
        text: 'Store Items',
        icon: <InventoryIcon />,
        path: '/admin/StoreMaster',
        menuKey: 'StockDashboard',
      },
      {
        text: 'Stores (Main / Sub)',
        icon: <InventoryIcon />,
        path: '/admin/StoreLocations',
        menuKey: 'StockDashboard',
      },
      {
        text: 'Stock',
        icon: <DashboardIcon />,
        path: '/admin/StockDashboard',
        menuKey: 'StockDashboard',
      },
      {
        text: 'Stock Transfer',
        icon: <InventoryIcon />,
        path: '/admin/StockTransfer',
        menuKey: 'StockDashboard',
      },
      {
        text: 'Stock History',
        icon: <DescriptionIcon />,
        path: '/admin/StockTransactionHistory',
        menuKey: 'StockTransactionHistory',
      },
      {
        text: 'Opening / Closing',
        icon: <DescriptionIcon />,
        path: '/admin/StockOpeningClosingReport',
        menuKey: 'StockDashboard',
      },
      {
        text: 'Kitchen Indent',
        icon: <AddBoxIcon />,
        path: '/admin/IndentEntry',
        menuKey: 'StorePurchase',
      },
      {
        text: 'Indent Orders List',
        icon: <DescriptionIcon />,
        path: '/admin/IndentList',
        menuKey: 'StorePurchase',
      },
      {
        // Canonical purchase/receiving screen: StorePurchase is wired to the
        // Store item master and can fulfil supplier indents. The older
        // PurchaseEntry (restaurant-item based) stays routable by URL but is no
        // longer the sidebar entry. Keyed on PurchaseEntry — the permission users
        // already hold — so existing access is unchanged.
        text: 'Purchase',
        icon: <AddBoxIcon />,
        path: '/admin/StorePurchase',
        menuKey: 'PurchaseEntry',
      },
      {
        // The older restaurant-item purchase screen, back in the menu on
        // request (2026-09-17). Same key as Purchase above.
        text: 'Purchase Entry',
        icon: <AddBoxIcon />,
        path: '/admin/PurchaseEntry',
        menuKey: 'PurchaseEntry',
      },
      {
        text: 'Suppliers',
        icon: <WorkIcon />,
        path: '/admin/SupplierMaster',
        menuKey: 'SupplierMaster',
      },
      {
        text: 'Recipes',
        icon: <InventoryIcon />,
        path: '/admin/RecipeMaster',
        menuKey: 'RecipeMaster',
      },
      {
        // Batch-wise expiry of store stock — previously only reachable via a
        // dashboard quick-action / direct URL. Now a first-class Store entry.
        text: 'Expiry Report',
        icon: <DescriptionIcon />,
        path: '/admin/ExpiryReport',
        menuKey: 'StockDashboard',
      },
    ],
  },
  // ══ FINANCE — money only ══
  {
    text: 'Accounts',
    icon: <MSym name='account_balance_wallet' />,
    children: [
      {
        text: 'Payments',
        icon: <MonetizationOnIcon />,
        path: '/admin/Paymentreport',
        menuKey: 'Paymentreport',
      },
      {
        text: 'Petty Cash',
        icon: <MonetizationOnIcon />,
        path: '/admin/pettycashmanagement',
        menuKey: 'PettyCashManagement',
      },
      {
        text: 'Customer Wallet',
        icon: <MonetizationOnIcon />,
        path: '/admin/Customerwallet',
        menuKey: 'Customerwallet',
      },
      {
        // Customers who still owe (orders settled as Due), with Collect.
        // Routed but unlinked until 2026-09-17. Its OWN key (Customer Dues).
        text: 'Customer Due Report',
        icon: <MonetizationOnIcon />,
        path: '/admin/CustomerDueReport',
        menuKey: 'CustomerDueReport',
      },
      {
        text: 'Corrections',
        icon: <DescriptionIcon />,
        path: '/admin/AmountCorrection',
        menuKey: 'ActivityLog',
      },
    ],
  },
  // ══ HR & PAYROLL — unchanged, already the model department ══
  {
    text: 'Staff',
    icon: <MSym name='groups' />,
    children: [
      {
        text: 'HR Dashboard',
        icon: <DashboardIcon />,
        path: '/admin/Hr/Dashboard',
        menuKey: 'HrDashboard',
      },
      {
        text: 'HR Reports',
        icon: <DescriptionIcon />,
        path: '/admin/Hr/Reports',
        menuKey: 'HrReports',
      },
      {
        text: 'Departments',
        icon: <WorkIcon />,
        path: '/admin/Hr/Departments',
        menuKey: 'HrDepartments',
      },
      {
        text: 'Designations',
        icon: <WorkIcon />,
        path: '/admin/Hr/Designations',
        menuKey: 'HrDesignations',
      },
      {
        text: 'Employees',
        icon: <WorkIcon />,
        path: '/admin/Hr/Employees',
        menuKey: 'HrEmployees',
      },
      {
        text: 'Shifts',
        icon: <WorkIcon />,
        path: '/admin/Hr/Shifts',
        menuKey: 'HrShifts',
      },
      {
        text: 'Attendance',
        icon: <WorkIcon />,
        path: '/admin/Hr/Attendance',
        menuKey: 'HrAttendance',
      },
      {
        text: 'Leave Types',
        icon: <WorkIcon />,
        path: '/admin/Hr/LeaveTypes',
        menuKey: 'HrLeaveTypes',
      },
      {
        text: 'Leave Requests',
        icon: <WorkIcon />,
        path: '/admin/Hr/LeaveRequests',
        menuKey: 'HrLeaveRequests',
      },
      {
        text: 'Overtime',
        icon: <WorkIcon />,
        path: '/admin/Hr/Overtime',
        menuKey: 'HrOvertime',
      },
      {
        text: 'Advance Salary',
        icon: <MonetizationOnIcon />,
        path: '/admin/Hr/AdvanceSalary',
        menuKey: 'HrAdvanceSalary',
      },
      {
        text: 'Loans',
        icon: <MonetizationOnIcon />,
        path: '/admin/Hr/Loans',
        menuKey: 'HrLoans',
      },
      {
        text: 'Salary Structure',
        icon: <MonetizationOnIcon />,
        path: '/admin/Hr/SalaryStructure',
        menuKey: 'HrSalaryStructure',
      },
      {
        text: 'Payroll',
        icon: <MonetizationOnIcon />,
        path: '/admin/Hr/Payroll',
        menuKey: 'HrPayroll',
      },
      {
        text: 'Salary Payments',
        icon: <MonetizationOnIcon />,
        path: '/admin/Hr/SalaryPayments',
        menuKey: 'HrSalaryPayments',
      },
      {
        text: 'Service Charge',
        icon: <MonetizationOnIcon />,
        path: '/admin/Hr/ServiceCharge',
        menuKey: 'HrServiceCharge',
      },
      {
        text: 'Tips',
        icon: <MonetizationOnIcon />,
        path: '/admin/Hr/Tips',
        menuKey: 'HrTips',
      },
      {
        text: 'Staff Meals',
        icon: <WorkIcon />,
        path: '/admin/Hr/StaffMeals',
        menuKey: 'HrStaffMeals',
      },
      {
        text: 'Uniform Deposit',
        icon: <WorkIcon />,
        path: '/admin/Hr/UniformDeposit',
        menuKey: 'HrUniformDeposit',
      },
    ],
  },
  // ══ REPORTS — every report in the software, one place ══
  {
    text: 'Reports',
    icon: <MSym name='analytics' />,
    children: [
      {
        text: 'Booking Report',
        icon: <DescriptionIcon />,
        path: '/admin/Bookingfilter',
        menuKey: 'Bookingfilter',
      },
      {
        // One booking's whole picture - details, package, discount, the CR/DR
        // ledger and paid/pending. Routed but unlinked until 2026-09-17. Its
        // OWN key: needs Scripts/Add_RestoredMenus_MenuKeys.sql.
        text: 'Booking Ledger',
        icon: <DescriptionIcon />,
        path: '/admin/BookingLedger',
        menuKey: 'BookingLedger',
      },
      {
        text: 'Guest Report',
        icon: <DescriptionIcon />,
        path: '/admin/Guestreport',
        menuKey: 'Guestreport',
      },
      {
        text: 'Night Audit',
        icon: <DescriptionIcon />,
        path: '/admin/NightAuditReport',
        menuKey: 'NightAuditReport',
      },
      {
        // Reuse the Booking Report permission key so these new reports inherit
        // the same visibility (the app hides a menu whose menuKey the user has
        // no grant for; a brand-new key would be hidden for everyone).
        text: 'Occupancy Report',
        icon: <DescriptionIcon />,
        path: '/admin/OccupancyReport',
        menuKey: 'BookingReport',
      },
      {
        text: 'Banquet Report',
        icon: <DescriptionIcon />,
        path: '/admin/BanquetReport',
        menuKey: 'BookingReport',
      },
      {
        text: 'Banquet Calendar',
        icon: <DescriptionIcon />,
        path: '/admin/BanquetCalendar',
        menuKey: 'BookingReport',
      },
      {
        text: 'Settlement Report',
        icon: <DescriptionIcon />,
        path: '/admin/SettlementReport',
        menuKey: 'BookingReport',
      },
      {
        text: 'Sale Report (Per Day)',
        icon: <DescriptionIcon />,
        path: '/admin/SaleReportPerDay',
        menuKey: 'BookingReport',
      },
      {
        // Refundable security deposits held / refunded — reuses the Booking
        // Report grant so it appears without a brand-new menu key.
        text: 'Security Deposits',
        icon: <DescriptionIcon />,
        path: '/admin/SecurityDepositReport',
        menuKey: 'BookingReport',
      },
      {
        text: 'Hold / Maintenance Rooms',
        icon: <DescriptionIcon />,
        path: '/admin/Holdreport',
        menuKey: 'Holdreport',
      },
      {
        text: 'Package Report',
        icon: <DescriptionIcon />,
        path: '/admin/packagereport',
        menuKey: 'packagereport',
      },
      {
        text: 'Add-On Amount',
        icon: <DescriptionIcon />,
        path: '/admin/AddonProfitReport',
        menuKey: 'AddonProfitReport',
      },
      {
        text: 'Room List',
        icon: <DescriptionIcon />,
        path: '/admin/rooms',
        menuKey: 'rooms',
      },
      {
        text: 'Invoice Report',
        icon: <DescriptionIcon />,
        path: '/admin/InvoiceReport',
        menuKey: 'InvoiceReport',
      },
      {
        text: 'Item Report',
        icon: <DescriptionIcon />,
        path: '/admin/ItemReport',
        menuKey: 'ItemReport',
      },
      // Order History is NOT listed here. It lives in the Restaurant Orders
      // tab strip, and that is where staff already are when they need it —
      // a second entry in Reports opened the same page from a place with no
      // tabs, so going back to Dine In meant leaving the report entirely.
      // The route and the tab are untouched.
      // The four restaurant reports now live behind ONE entry: they open the
      // Restro Report workspace, which carries a tab strip across all four.
      // Four separate rows here meant leaving the set to reach the next one.
      {
        text: 'Restro Report',
        icon: <DescriptionIcon />,
        path: '/admin/RestaurantInvoiceReport',
        menuKey: 'RestaurantInvoiceReport',
      },
      {
        text: 'Booking History',
        icon: <DescriptionIcon />,
        path: '/admin/BookingHistory',
        menuKey: 'RestaurantInvoiceReport',
      },
      {
        // Carried over from the pre-IA menu: the /admin/AllCategory route is
        // still live, so it keeps a home under Reports instead of being
        // orphaned (its old "Restaurant" group no longer exists).
        text: 'All Category Report',
        icon: <DescriptionIcon />,
        path: '/admin/AllCategory',
        menuKey: 'AllCategory',
      },
      {
        text: 'Purchase Reports',
        icon: <DescriptionIcon />,
        path: '/admin/PurchaseReports',
        menuKey: 'PurchaseReports',
      },
      {
        // Routed and reachable by URL all along, but with no way in from the
        // menu. Sits beside Purchase Reports because that is where anyone
        // looking for purchase figures will already be.
        text: 'Purchase Ledger',
        icon: <DescriptionIcon />,
        path: '/admin/PurchaseLedgerReport',
        menuKey: 'PurchaseLedgerReport',
      },
      {
        text: 'Agent Report',
        icon: <DescriptionIcon />,
        path: '/admin/AgentReport',
        menuKey: 'AgentReport',
      },
      {
        text: 'Agent Commission',
        icon: <DescriptionIcon />,
        path: '/admin/Agentcommissionreport',
        menuKey: 'Agentcommissionreport',
      },
      {
        // The older agent commission screen (AgentCommision.js), back in the
        // menu on request (2026-09-17). "(Old)" so it is not mistaken for the
        // entry above, which is also titled Agent Commission Report. Its OWN
        // key: needs Scripts/Add_RestoredMenus_MenuKeys.sql.
        text: 'Agent Commission (Old)',
        icon: <DescriptionIcon />,
        path: '/admin/AgentCommision',
        menuKey: 'AgentCommision',
      },
      {
        text: 'Activity Log',
        icon: <DescriptionIcon />,
        path: '/admin/ActivityLog',
        menuKey: 'ActivityLog',
      },
      {
        text: 'GST Reports',
        icon: <DescriptionIcon />,
        path: '/admin/GstReports',
        menuKey: 'GstReports',
      },
      {
        text: 'Subscription Payments',
        icon: <MonetizationOnIcon />,
        path: '/admin/SubscriptionPaymentReport',
        // Its own permission key, and now actually enforced. What the property
        // has paid us is owner's business; it was on show to anyone who could
        // open Reports.
        menuKey: 'SubscriptionPaymentReport',
      },
    ],
  },
  // ══ MARKETING — reaching customers again (hotel and restaurant alike) ══
  // Its own department, not part of Setup: it is used every week, not once.
  // NOT in RESTAURANT_MENUS / HOTEL_MENUS - both kinds of property market.
  // Needs Scripts/Marketing_Step1.sql (menu keys + MarketingConsents).
  {
    text: 'Marketing',
    icon: <MSym name='campaign' />,
    children: [
      {
        text: 'Marketing Dashboard',
        icon: <MSym name='insights' />,
        path: '/admin/Marketing/Dashboard',
        menuKey: 'MarketingDashboard',
      },
      {
        text: 'Customers',
        icon: <MSym name='groups' />,
        path: '/admin/Marketing/Customers',
        menuKey: 'MarketingCustomers',
      },
      {
        // WhatsApp offers to a customer list; sent in the background.
        text: 'Campaigns',
        icon: <MSym name='send' />,
        path: '/admin/Marketing/Campaigns',
        menuKey: 'MarketingCampaigns',
      },
      {
        // Birthday / win-back / thank-you / welcome - sent by themselves.
        text: 'Automations',
        icon: <MSym name='autorenew' />,
        path: '/admin/Marketing/Automations',
        menuKey: 'MarketingAutomations',
      },
      {
        // Loyalty points - program rules + members and their points history.
        text: 'Loyalty Points',
        icon: <MSym name='loyalty' />,
        path: '/admin/Marketing/Loyalty',
        menuKey: 'MarketingLoyalty',
      },
      {
        text: 'Marketing Settings',
        icon: <MSym name='tune' />,
        path: '/admin/Marketing/Settings',
        menuKey: 'MarketingSettings',
      },
    ],
  },
  // ══ MASTERS (SETUP) — configuration only, done rarely ══
  {
    text: 'Setup',
    icon: <MSym name='settings' />,
    children: [
      {
        text: 'My Hotel',
        icon: <HotelIcon />,
        path: '/admin/hotels',
        menuKey: 'hotels',
      },
      {
        text: 'Rooms',
        icon: <MeetingRoomIcon />,
        path: '/admin/rooms/add',
        menuKey: 'rooms/add',
      },
      {
        text: 'Room Types',
        icon: <SettingsIcon />,
        path: '/admin/roomtype',
        menuKey: 'roomtype',
      },
      {
        text: 'Packages',
        icon: <SettingsIcon />,
        path: '/admin/managepackage',
        menuKey: 'managepackage',
      },
      {
        text: 'Extras',
        icon: <SettingsIcon />,
        path: '/admin/Addonm',
        menuKey: 'Addonm',
      },
      {
        // Single entry to the 9-tab Restaurant Masters workspace.
        text: 'Restaurant Setup',
        icon: <SettingsIcon />,
        path: '/admin/AddItem',
        menuKey: 'AddItem',
      },
      {
        text: 'QR Codes',
        icon: <SettingsIcon />,
        path: '/admin/QrCodeGenerator',
        menuKey: 'CreateOrder',
      },
      {
        // Swiggy / Zomato through the WeraFoods gateway: the outlet ids, what
        // goes online, and pushing the menu to those apps.
        //
        // The page and its route have existed since 2026-09-08 and the key was
        // in the database, but this ENTRY was never added - so the screen could
        // not appear in anybody's sidebar however they were granted, and the
        // route guard above (which matches on these paths) had nothing to match
        // and let anyone in by URL. One missing row, both halves of the bug.
        //
        // 'Online Ordering' is already listed in RESTAURANT_MENUS, so a hotel
        // with no restaurant has never been shown it.
        text: 'Online Ordering',
        icon: <MSym name='storefront' />,
        path: '/admin/OnlineOrdering',
        menuKey: 'OnlineOrdering',
      },
      {
        // Accounting systems (Tally | SAP tabs). Its own key, so it can be
        // given per plan and per user like any other page, and NOT in
        // RESTAURANT_MENUS: a hotel with no restaurant keeps books too.
        // Needs Scripts/Add_AccountingIntegration_MenuKey.sql.
        text: 'Tally & SAP',
        icon: <MSym name='sync_alt' />,
        path: '/admin/AccountingIntegration',
        menuKey: 'AccountingIntegration',
      },
      {
        text: 'Agents',
        icon: <WorkIcon />,
        path: '/admin/Agent',
        menuKey: 'Agent',
      },
      {
        text: 'Coupons',
        icon: <SettingsIcon />,
        path: '/admin/Couponmaster',
        menuKey: 'Couponmaster',
      },
      {
        // The property's own details — the supplier block, bank and UPI that
        // print on every bill. Scoped to the signed-in company by the token,
        // so it is safe to grant to a manager without exposing other tenants.
        text: 'Company Settings',
        icon: <SettingsIcon />,
        path: '/admin/CompanySettings',
        menuKey: 'CompanySettings',
      },
      {
        // Company-wide preferences that are not printed on a bill: what the
        // notification bell says and in which language (Sound Settings).
        text: 'Business Settings',
        icon: <SettingsIcon />,
        path: '/admin/BusinessSettings',
        menuKey: 'BusinessSettings',
      },
      {
        // The property's own public website: designed, published and its menu
        // chosen here, in one page with two tabs. The site itself is served
        // by the renderer (Webthemebuilder).
        text: 'Front Website',
        icon: <SettingsIcon />,
        path: '/admin/WebsiteBuilder',
        menuKey: 'WebsiteBuilder',
      },
      {
        // Granted like everything else now.
        //
        // This one had the best argument for staying always-on - somebody who
        // cannot read the interface cannot navigate to the screen that would
        // fix that - but the rule is the rule, and nobody in the database holds
        // this key today, so Scripts/Grant_LanguageSettings_AllUsers.sql gives
        // it to every existing user. Run it WITH this deploy, or the language
        // switcher disappears for all of them.
        text: 'Language',
        icon: <MSym name='translate' />,
        path: '/admin/LanguageSettings',
        menuKey: 'LanguageSettings',
      },
    ],
  },
  // ══ ADMINISTRATION — users, roles, tenants, support console ══
  {
    text: 'Users',
    icon: <MSym name='manage_accounts' />,
    children: [
      {
        text: 'Add User',
        icon: <WorkIcon />,
        path: '/admin/CreateUser',
        menuKey: 'CreateUser',
      },
      {
        text: 'User Access',
        icon: <SettingsIcon />,
        path: '/admin/UserPermission',
        menuKey: 'UserPermission',
      },
      {
        text: 'Roles',
        icon: <SettingsIcon />,
        path: '/admin/RbacRoles',
        menuKey: 'UserPermission',
      },
      {
        text: 'Special Access',
        icon: <SettingsIcon />,
        path: '/admin/RbacUserOverrides',
        menuKey: 'UserPermission',
      },
      {
        text: 'User Report',
        icon: <DescriptionIcon />,
        path: '/admin/UserReport',
        menuKey: 'UserReport',
      },
      {
        // SUPER ADMIN ONLY — gated on Users.isMaster (children filter), so a
        // normal company user never sees this regardless of menuKey grants.
        text: 'Companies',
        icon: <DashboardIcon />,
        path: '/admin/SuperAdminCompanies',
        menuKey: 'SuperAdminCompanies',
        masterOnly: true,
      },
      {
        // SUPER ADMIN ONLY — DB-driven menu management.
        text: 'Menus',
        icon: <SettingsIcon />,
        path: '/admin/MenuMaster',
        menuKey: 'MenuMaster',
        masterOnly: true,
      },
      {
        // Master-admin only (IsMaster) — manage all companies' complaints.
        text: 'Help Console',
        icon: <DashboardIcon />,
        path: '/admin/SupportAdmin',
        menuKey: 'SupportAdmin',
        masterOnly: true,
      },
    ],
  },
  // ── Support kept at the very bottom of the menu ──
  {
    text: 'Help',
    icon: <MSym name='support_agent' />,
    path: '/admin/SupportTickets',
    menuKey: 'SupportTickets',
  },
];

/* ── Sidebar nav item (parent/standalone) ── */
function NavItem({ menu, isOpen, isActive, expanded, onClick }) {
  return (
    <Tooltip title={expanded ? '' : menu.text} placement='right' arrow>
      <ListItemButton
        onClick={onClick}
        sx={{
          mx: '12px',
          minHeight: 48,
          borderRadius: '14px',
          mb: '4px',
          px: expanded ? '18px' : '10px',
          justifyContent: expanded ? 'initial' : 'center',
          position: 'relative',
          // Active = soft filled "pill" highlight — a rounded tinted block
          // behind the item (idea from the reference), in brand red on the
          // navy sidebar. No left bar; the fill + hairline border define it.
          background: isActive
            ? 'linear-gradient(180deg, rgba(59,130,246,0.20), rgba(37,99,235,0.13))'
            : 'transparent',
          border: isActive
            ? '1px solid rgba(96,165,250,0.34)'
            : '1px solid transparent',
          boxShadow: isActive ? S.cardShadow : 'none',
          color: isActive ? '#60A5FA' : S.textDim,
          transition:
            'background 180ms ease, color 180ms ease, box-shadow 180ms ease',
          '&:hover': {
            background: isActive
              ? 'linear-gradient(180deg, rgba(59,130,246,0.24), rgba(37,99,235,0.16))'
              : S.bgHover,
            color: isActive ? '#93C5FD' : S.textHover,
          },
        }}>
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: expanded ? '14px' : 'auto',
            justifyContent: 'center',
            color: isActive ? '#60A5FA' : S.textDim,
            transition: 'color 180ms ease',
            '& svg': { fontSize: 22 },
          }}>
          {menu.icon}
        </ListItemIcon>
        {expanded && (
          <ListItemText
            primary={menu.text}
            primaryTypographyProps={{
              fontSize: '15px',
              fontWeight: isActive ? 600 : 500,
              fontFamily: 'Inter,sans-serif',
              letterSpacing: '0.1px',
              color: 'inherit',
              noWrap: true,
            }}
          />
        )}
        {expanded && menu.children && (
          <Box
            sx={{
              color: S.textDim,
              opacity: 0.9,
              ml: '4px',
              display: 'flex',
              alignItems: 'center',
            }}>
            {isOpen ? (
              <ExpandLess sx={{ fontSize: 18 }} />
            ) : (
              <ExpandMore sx={{ fontSize: 18 }} />
            )}
          </Box>
        )}
      </ListItemButton>
    </Tooltip>
  );
}

/* ── Sidebar child item ── */
function ChildItem({ child, isActive, expanded, onClick }) {
  return (
    <Tooltip title={expanded ? '' : child.text} placement='right' arrow>
      <ListItemButton
        onClick={onClick}
        sx={{
          mx: '12px',
          minHeight: 40,
          borderRadius: '12px',
          mb: '2px',
          pl: expanded ? '46px' : '10px',
          pr: '12px',
          justifyContent: expanded ? 'initial' : 'center',
          position: 'relative',
          background: isActive ? 'rgba(37,99,235,0.18)' : 'transparent',
          color: isActive ? '#60A5FA' : S.textDim,
          transition: 'background 180ms ease, color 180ms ease',
          '&:hover': {
            background: S.bgHover,
            color: isActive ? '#93C5FD' : S.textHover,
          },
        }}>
        {expanded && (
          <Box
            sx={{
              width: isActive ? 6 : 5,
              height: isActive ? 6 : 5,
              borderRadius: '50%',
              bgcolor: isActive ? '#60A5FA' : '#8A94A6',
              position: 'absolute',
              left: '26px',
              top: '50%',
              transform: 'translateY(-50%)',
              transition: 'all 180ms ease',
            }}
          />
        )}
        {!expanded && <ArrowIcon sx={{ fontSize: 16, color: 'inherit' }} />}
        {expanded && (
          <ListItemText
            primary={child.text}
            primaryTypographyProps={{
              fontSize: '14px',
              fontWeight: isActive ? 600 : 500,
              fontFamily: 'Inter,sans-serif',
              letterSpacing: '0.1px',
              color: 'inherit',
            }}
          />
        )}
      </ListItemButton>
    </Tooltip>
  );
}

export default function AdminLayout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── Session-derived identity/branding — read FRESH on every render ──
  // These MUST live inside the component (not module scope). At module-load
  // time (the login screen) companyDetails isn't in sessionStorage yet, so a
  // module-level read would cache isRestaurantOnly=false forever and route a
  // restaurant login (propertType 'R') to the hotel dashboard. Rule:
  //   propertType 'R' → restaurant  |  propertType 'H' → hotel.
  const company = JSON.parse(
    sessionStorage.getItem('companyDetails') || 'null',
  );
  const Users = JSON.parse(sessionStorage.getItem('user') || 'null');
  const userName = Users?.name || 'Admin';
  const isRestaurantOnly =
    (company?.propertType || sessionStorage.getItem('propertType')) === 'R';
  // #2: wizard-driven module visibility. A restaurant-only property has NO hotel;
  // a hotel shows restaurant modules only if it actually configured a restaurant.
  const restaurantEnabled = isRestaurantOnly || company?.hasRestaurant === true;
  const hotelEnabled = !isRestaurantOnly;
  const userRole =
    Users?.role || (isRestaurantOnly ? 'Restaurant Staff' : 'Hotel Staff');
  // Domain-aware brand: restaurant-only property → "DineMaster", else "Hotel Desk".
  const APP_BRAND = getBrand();
  const brandName = APP_BRAND.name;
  const brandSub = APP_BRAND.sub;
  const userInitials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [openMenus, setOpenMenus] = useState({});
  const [filteredMenu, setFilteredMenu] = useState([]);
  const createOrderPinnedExpandedRef = useRef(false);

  // ── Inventory Alerts (notification bell) — batch expiry, auto-refreshed ──
  const [invAlerts, setInvAlerts] = useState({ summary: {}, alerts: [] });
  const [bellAnchor, setBellAnchor] = useState(null);
  const invTotal =
    (invAlerts.summary?.expired || 0) +
    (invAlerts.summary?.today || 0) +
    (invAlerts.summary?.next7 || 0) +
    (invAlerts.summary?.next30 || 0);

  const [expandedAlert, setExpandedAlert] = useState(null);
  const [invBusy, setInvBusy] = useState(false);

  // ── QR orders + invoice requests, folded into the same bell ─────────────
  //
  // These used to be two more bells, sitting in the Restaurant dashboard's
  // hero card. Three bells in two different places meant a cashier had to
  // remember where each kind of alert lived, and neither of the dashboard two
  // existed anywhere else - leave that page and the notifications stopped.
  //
  // Here they are always mounted, so the live stream runs on every screen.
  const [qrPending, setQrPending] = useState([]);
  const [invoiceReqs, setInvoiceReqs] = useState([]);
  const [bellLive, setBellLive] = useState(false);

  // Orders placed from the waiter APP.
  //
  // Unlike QR orders (which wait for approval) and invoice requests (which
  // wait to be billed), an app order is already live - its KOT has printed
  // and it is on its table. So there is no server-side "pending" list to ask
  // for: the SSE signal IS the notification, and this list is what the bell
  // shows until somebody opens it. Kept in sessionStorage so a page refresh
  // in the same tab does not lose the last few.
  const APP_ORDERS_KEY = 'bellAppOrders';
  const [appOrders, setAppOrders] = useState(() => {
    try {
      const raw = sessionStorage.getItem(APP_ORDERS_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  });
  const storeAppOrders = (list) => {
    try {
      sessionStorage.setItem(APP_ORDERS_KEY, JSON.stringify(list));
    } catch {
      /* private mode: the list still works for this page */
    }
  };
  const addAppOrder = (entry) => {
    setAppOrders((prev) => {
      // One row per order, newest first, and never a wall of them.
      const next = [entry, ...prev.filter((x) => x.orderId !== entry.orderId)].slice(0, 20);
      storeAppOrders(next);
      return next;
    });
  };
  // Opening one answers it: the row goes, and Table Orders opens on that
  // order (same link the invoice-request rows use).
  const openAppOrder = (o) => {
    setBellAnchor(null);
    setAppOrders((prev) => {
      const next = prev.filter((x) => x.orderId !== o.orderId);
      storeAppOrders(next);
      return next;
    });
    const type = o.isRoom ? 'Room' : 'DineIn';
    navigate(`/admin/TableOrders?type=${type}&orderId=${o.orderId}`);
    try {
      window.dispatchEvent(
        new CustomEvent('tableOrdersOpen', { detail: { orderId: o.orderId, type } }),
      );
    } catch {
      /* nothing listening */
    }
  };
  const clearAppOrders = () => {
    setAppOrders([]);
    storeAppOrders([]);
  };

  // The bell's voice: chime + "order received from table / room N", in the
  // language the company chose under Setup → Business Settings → Sound
  // Settings. Lives here because the bell does - every screen, one source.
  const notifSound = useNotificationSound(getCompanyId());
  // POS full screen: this layout's bar and sidebar step aside for the till.
  const posFullscreen = usePosFullscreen();
  const announceRef = useRef(notifSound.announce);
  useEffect(() => {
    announceRef.current = notifSound.announce;
  }, [notifSound.announce]);

  // Cache the company's "description on KOT / bill / invoice" switches so the
  // synchronous ESC/POS builders can honour them (see descFlags.js). Best-effort.
  useEffect(() => {
    try {
      const cmp = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
      const token = sessionStorage.getItem('token');
      if (!cmp?.id || !token) return;
      fetch(`${config.API_BASE_URL}/qrmenu/desc-display/${cmp.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d?.json) { try { setDescFlagsCache(JSON.parse(d.json)); } catch { /* keep defaults */ } }
        })
        .catch(() => {});
    } catch { /* ignore */ }
  }, []);
  // Ids already seen, so a refetch that returns the same rows does not ring
  // again - only a genuinely NEW order does. The very first load never rings,
  // or every login would sound like a rush of orders.
  const qrKnownRef = useRef(new Set());
  const qrFirstLoadRef = useRef(true);
  // Same for invoice requests from the waiter app: "invoice requested for
  // order N (table)", once per request.
  const invKnownRef = useRef(new Set());
  const invFirstLoadRef = useRef(true);
  // App orders have no pending list to diff against - the signal itself
  // carries the table - so the bell speaks on the signal, once per order.
  const appSeenRef = useRef(new Set());

  // Loaded together, and quietly: a failed alert count must never be the
  // reason a page shows an error.
  const loadBellQueues = useCallback(async () => {
    const cid = getCompanyId();
    if (!cid) return;
    const headers = { Authorization: `Bearer ${sessionStorage.getItem('token')}` };

    try {
      const r = await fetch(`${config.API_BASE_URL}/Home/PendingQrOrders/${cid}`, { headers });
      if (r.ok) {
        const d = await r.json();
        const rows = Array.isArray(d) ? d : [];
        // Announce ids that were not in the last answer. Works the same
        // whether the stream said "something arrived" or the fallback poll
        // found it, and never twice for one order.
        // One key per guest submission (order + batch): one order can
        // carry two waiting at once.
        const qrKey = (o) => `${o.orderId}-${o.batchId || 0}`;
        const fresh = rows.filter((o) => !qrKnownRef.current.has(qrKey(o)));
        // Replaced, not added to - see the invoice-request note below.
        qrKnownRef.current = new Set(rows.map(qrKey));
        if (!qrFirstLoadRef.current) {
          fresh.forEach((o) =>
            announceRef.current('qrOrder', {
              isRoom: o.isRoom === true,
              no: o.isRoom === true && o.roomNo ? o.roomNo : o.tableNo,
            }),
          );
        }
        qrFirstLoadRef.current = false;
        setQrPending(rows);
      }
    } catch {
      /* the next signal or the fallback poll tries again */
    }

    try {
      // unread=true: a request somebody has already clicked open is gone from
      // the bell (still Pending for the dashboard panel until invoiced).
      //
      // "true", not "1". The parameter binds to a C# bool, and ASP.NET Core
      // accepts only true/false for those - "1" fails model binding and the
      // whole request comes back 400, which is how the bell went silent.
      const r = await fetch(`${config.API_BASE_URL}/invoicerequest/pending/${cid}?unread=true`, { headers });
      if (r.ok) {
        const d = await r.json();
        const items = Array.isArray(d?.items) ? d.items : [];
        const fresh = items.filter((it) => !invKnownRef.current.has(it.id));
        // The known set is REPLACED, not added to. A request that left the
        // list (opened here, or handled elsewhere) is forgotten, so if the
        // waiter asks again and it comes back, it rings again. Adding for
        // ever meant a re-raised request arrived in silence.
        invKnownRef.current = new Set(items.map((it) => it.id));
        if (!invFirstLoadRef.current) {
          fresh.forEach((it) => {
            announceRef.current('invoiceRequest', {
              order: it.orderId,
              no: it.tableno || '',
            });
            // Said aloud, shown in the bell, AND on screen - a cashier looking
            // at another page should not have to notice a badge.
            try {
              toast.info(
                it.message || `Invoice requested for order #${it.orderId}`,
                { autoClose: 8000 },
              );
            } catch {
              /* the bell row is still there */
            }
          });
        }
        invFirstLoadRef.current = false;
        setInvoiceReqs(items);
      }
    } catch {
      /* as above */
    }
  }, []);

  // Clicking an invoice request answers it: the row leaves the bell (marked
  // read on the server, so it stays gone on every other till too) and the
  // order opens on Table Orders, filtered to that order number. The request
  // itself stays Pending until the bill is actually generated there.
  const openInvoiceRequest = async (r) => {
    setBellAnchor(null);
    setInvoiceReqs((list) => list.filter((x) => x.id !== r.id));
    try {
      await fetch(`${config.API_BASE_URL}/invoicerequest/read/${r.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
      });
    } catch {
      /* the row is already off this bell; the next refetch settles it */
    }
    const type = r.tableno && String(r.tableno).trim() ? 'DineIn' : 'Room';
    navigate(`/admin/TableOrders?type=${type}&orderId=${r.orderId}`);
    // Already on Table Orders? The screen read its URL on mount and will not
    // read it again, so tell it directly.
    try {
      window.dispatchEvent(
        new CustomEvent('tableOrdersOpen', { detail: { orderId: r.orderId, type } }),
      );
    } catch {
      /* nothing listening */
    }
  };

  // The live stream. One per session now, rather than one per screen that
  // happened to render a bell.
  useEffect(() => {
    loadBellQueues();
    const close = openNotifyStream({
      onOpen: () => {
        setBellLive(true);
        loadBellQueues();
      },
      onClose: () => setBellLive(false),
      onEvent: (name, data) => {
        // The signal only says something arrived; the lists still come from
        // the API, because two tills both get this and only the server knows
        // what is still unattended.
        if (
          name === 'qrOrderPlaced' ||
          name === 'qrOrderResolved' ||
          name === 'invoiceRequested'
        )
          loadBellQueues();
        // "Order received on Garden-05 from app." Nothing to fetch: an app
        // order is an ordinary order, already on its table.
        if (name === 'appOrderPlaced' && data) {
          const key = `${data.orderId}|${data.at || ''}`;
          if (!appSeenRef.current.has(key)) {
            appSeenRef.current.add(key);
            const isRoom = data.isRoom === true;
            announceRef.current('appOrder', {
              isRoom,
              no: isRoom && data.roomNo ? data.roomNo : data.tableNo,
            });
            // ...and the bell SHOWS it, not just says it.
            const where = isRoom
              ? `Room ${data.roomNo || ''}`.trim()
              : data.tableNo || 'the app';
            addAppOrder({
              orderId: data.orderId,
              tableNo: data.tableNo || '',
              roomNo: data.roomNo || '',
              isRoom,
              at: data.at || new Date().toISOString(),
            });
            // A toast as well: the badge is a number in the corner, and a
            // cashier working on another screen should not have to notice it.
            try {
              toast.info(`New app order on ${where} · #${data.orderId}`, {
                autoClose: 6000,
              });
            } catch {
              /* the bell row is still there */
            }
          }
        }
        // A Swiggy / Zomato order from the WeraFoods webhook: "new Swiggy
        // order, number 4396" (the last 4 digits - the full number is 15).
        // Only for users who may open Restaurant Orders → Swiggy / Zomato:
        // that switch is opt-in, so a waiter without it hears nothing.
        if (name === 'onlineOrderPlaced' && data) {
          const key = `online|${data.id}`;
          if (
            !appSeenRef.current.has(key) &&
            hasStoredPermission(PAGE_RBAC['/admin/OnlineOrders'])
          ) {
            appSeenRef.current.add(key);
            const platform = data.platform || 'online';
            const orderNo = String(data.orderNo || '');
            announceRef.current(
              String(platform).toLowerCase() === 'zomato' ? 'zomatoOrder' : 'swiggyOrder',
              { order: orderNo.slice(-4) },
            );
            try {
              toast.info(`New ${platform} order ${orderNo} - open Restaurant Orders → Swiggy / Zomato`, {
                autoClose: 10000,
              });
            } catch {
              /* the Swiggy / Zomato tab still lists it */
            }
          }
          // The Swiggy / Zomato list, if open, refreshes at once.
          try {
            window.dispatchEvent(new CustomEvent('onlineOrderPlaced', { detail: data }));
          } catch {
            /* nothing listening */
          }
        }
      },
    });
    return close;
  }, [loadBellQueues]);

  // A screen in THIS tab that just approved / rejected / handled something
  // says so directly, so the bell is right at once even if the stream is
  // down and the server's signal never arrives.
  useEffect(() => {
    const on = () => loadBellQueues();
    window.addEventListener('bellRefresh', on);
    return () => window.removeEventListener('bellRefresh', on);
  }, [loadBellQueues]);

  // Two polls, because a live stream is not a guarantee.
  //
  //   stream down  → every 30s, the fallback that keeps the bell working.
  //   stream up    → every 60s anyway, as a safety net.
  //
  // The safety net exists because "connected" only means the connection is
  // open: a signal the SERVER never sent (an older API build, a controller
  // that forgot to publish) is indistinguishable from nothing happening, and
  // without a poll such a notification NEVER arrived until the page was
  // reloaded. That is exactly how an invoice request could sit unread in the
  // database while the bell showed nothing. One request a minute is a price
  // worth paying to make the bell self-healing.
  useEffect(() => {
    const t = setInterval(loadBellQueues, bellLive ? 60000 : 30000);
    return () => clearInterval(t);
  }, [bellLive, loadBellQueues]);

  // ── Global search ────────────────────────────────────────────────────
  // The header search used to match MENU NAMES only, so a receptionist could
  // navigate but never find a guest. It now searches real records as well and
  // keeps the menu results underneath, so nobody loses the navigation they
  // already rely on.
  //
  // The server decides which actions each result supports (a checked-out
  // booking must not offer Check-in), so this component never re-derives
  // business rules it does not own.
  const [searchText, setSearchText] = useState('');
  // Phone: the search is an ICON until it is asked for. A permanent field on
  // a 412px bar left nothing for the logo and pushed the speaker and the bell
  // on top of it. Desktop always shows the field, so this flag is ignored
  // from sm up.
  const [topSearchOpen, setTopSearchOpen] = useState(false);
  // The field is always mounted (just hidden), so autoFocus would never fire
  // - it only runs on mount. Focus it when it is actually revealed, or the
  // user taps the icon and then has to tap the box as well.
  const topSearchInputRef = useRef(null);
  useEffect(() => {
    if (!topSearchOpen) return undefined;
    const t = setTimeout(() => topSearchInputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [topSearchOpen]);
  const [searchHits, setSearchHits] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);

  useEffect(() => {
    const q = searchText.trim();
    if (q.length < 2) {
      setSearchHits([]);
      return undefined;
    }
    // Debounced: this endpoint fires on every keystroke of every user.
    const timer = setTimeout(async () => {
      try {
        const cid = getCompanyId();
        if (!cid) return;
        setSearchBusy(true);
        const token = sessionStorage.getItem('token');
        const res = await fetch(
          `${config.API_BASE_URL}/search?q=${encodeURIComponent(q)}&companyId=${cid}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) {
          setSearchHits([]);
          return;
        }
        const d = await res.json();
        setSearchHits(Array.isArray(d?.hits) ? d.hits : []);
      } catch {
        setSearchHits([]);
      } finally {
        setSearchBusy(false);
      }
    }, 280);
    return () => clearTimeout(timer);
  }, [searchText]);
  // ── OTA bookings waiting to be imported (same bell) ──
  // A booking pushed by the channel manager sits in Channel Manager until
  // someone imports it. Without a signal here nobody knows it arrived until
  // they happen to open that screen - which is how an OTA room gets sold twice.
  const [otaPending, setOtaPending] = useState({ total: 0, recent: [] });

  const loadInvAlerts = async () => {
    try {
      const cid = getCompanyId();
      if (!cid) return;
      const token = sessionStorage.getItem('token');
      const res = await fetch(
        `${config.API_BASE_URL}/stockreport/expiry-summary/${cid}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) return;
      setInvAlerts(await res.json());
    } catch {
      /* silent — bell just shows nothing */
    }
  };
  const loadOtaPending = async () => {
    try {
      const cid = getCompanyId();
      if (!cid) return;
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${config.API_BASE_URL}/channel/pending/${cid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const d = await res.json();
      setOtaPending({ total: d?.total || 0, recent: d?.recent || [] });
    } catch {
      /* silent - channel manager may simply not be set up */
    }
  };

  useEffect(() => {
    loadInvAlerts();
    loadOtaPending();
    const t = setInterval(() => {
      loadInvAlerts();
      loadOtaPending();
    }, 60000); // scheduled auto-refresh
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const openExpiry = (bucket) => {
    setBellAnchor(null);
    navigate(
      bucket ? `/admin/ExpiryReport?bucket=${bucket}` : '/admin/ExpiryReport',
    );
  };
  const alertColor = (s) =>
    s === 'Expired' || s === 'Today'
      ? '#DC2626'
      : s === 'Next7'
        ? '#EA580C'
        : s === 'Next30'
          ? '#CA8A04'
          : '#16A34A';
  const alertDays = (d) =>
    d == null
      ? ''
      : d < 0
        ? `Expired ${Math.abs(d)}d ago`
        : d === 0
          ? 'Expires today'
          : d === 1
            ? 'Expires tomorrow'
            : `Expires in ${d} days`;

  // ── Smart Action Center ── permission (fail-open, matches RBAC default) ──
  const canInvManage = () => {
    try {
      const raw = sessionStorage.getItem('permissionCodes');
      if (!raw) return true;
      const codes = JSON.parse(raw);
      if (!Array.isArray(codes) || codes.length === 0) return true;
      return codes
        .map((c) => String(c).toUpperCase())
        .includes('INVENTORY_MANAGER');
    } catch {
      return true;
    }
  };
  const disposeBatch = async (a) => {
    if (invBusy) return;
    if (
      !window.confirm(
        `Mark "${a.itemName}" (batch ${a.batchNumber || '—'}) for disposal? Its available quantity will be written off.`,
      )
    )
      return;
    setInvBusy(true);
    try {
      const cid = getCompanyId();
      const uid = sessionStorage.getItem('userId') || '';
      const token = sessionStorage.getItem('token');
      const res = await fetch(
        `${config.API_BASE_URL}/inventory/dispose-batch/${a.batchId}?companyId=${cid}&userId=${uid}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) throw new Error();
      setExpandedAlert(null);
      await loadInvAlerts();
    } catch {
      /* ignore */
    } finally {
      setInvBusy(false);
    }
  };
  // Context-aware actions per alert: driven by expiry status + permission.
  const alertActions = (a) => {
    const expired = a.daysRemaining < 0;
    const mgr = canInvManage();
    const acts = [];
    if (!expired) {
      acts.push({
        icon: 'restaurant',
        label: 'Issue to Kitchen',
        onClick: () => {
          setBellAnchor(null);
          navigate('/admin/StockTransfer');
        },
      });
      acts.push({
        icon: 'swap_horiz',
        label: 'Transfer Store',
        onClick: () => {
          setBellAnchor(null);
          navigate('/admin/StockTransfer');
        },
      });
    } else {
      acts.push({
        icon: 'visibility',
        label: 'View Batch Details',
        onClick: () => openExpiry('expired'),
      });
      if (!mgr)
        acts.push({
          icon: 'approval',
          label: 'Request Manager Approval',
          onClick: () =>
            window.alert('Approval request noted for your Inventory Manager.'),
        });
    }
    if (mgr)
      acts.push({
        icon: 'delete',
        label: 'Mark for Disposal',
        danger: true,
        onClick: () => disposeBatch(a),
      });
    return acts;
  };

  // When the subscription is expired / missing, lock the menu behind an
  // overlay (logo + Upgrade + Logout stay usable). Driven by the backend.
  const [subLocked, setSubLocked] = useState(false);
  // Plan-wise menu access: { allMenus, menuKeys }. null = not loaded yet.
  const [planAccess, setPlanAccess] = useState(null);
  // The lapsed subscription was a free trial, so the panel below asks for the
  // administrator rather than offering an upgrade.
  const [trialLapsed, setTrialLapsed] = useState(false);

  useEffect(() => {
    const cid = getCompanyId();
    if (!cid) return;
    subscriptionApi
      .getStatus(cid)
      .then((res) => {
        setSubLocked(res?.data?.canAccess === false);
        // A lapsed FREE TRIAL is not a renewal — the account waits on the
        // administrator, so this panel must not tell them to go and buy.
        setTrialLapsed(Boolean(res?.data?.isExpired && res?.data?.isTrial));
      })
      .catch(() => setSubLocked(false));
    subscriptionApi
      .getCompanyMenus(cid)
      .then((res) => setPlanAccess(res?.data || { allMenus: true }))
      .catch(() => setPlanAccess({ allMenus: true })); // fail open on error
  }, []);

  // #3: DIRECT-URL ACCESS GUARD. The sidebar already hides menus a user lacks,
  // but the page itself stayed reachable by typing its URL. Mirror the SAME
  // per-user menuKey grant rule here and bounce an unauthorized deep-link back
  // to a safe page. Fail-open by design — master admins, and any time the
  // permission list isn't present, are never blocked — so it can never lock a
  // legitimate user out of the app.
  useEffect(() => {
    if (Users?.isMaster) return;
    const raw = sessionStorage.getItem('permissions');
    if (!raw) return; // permissions not loaded yet -> don't block
    let keys;
    try {
      keys = new Set(JSON.parse(raw).map((p) => p.menuKey));
    } catch {
      return;
    }

    // Flatten the static menu into guardable {path, menuKey} entries.
    const flat = [];
    const walk = (arr) =>
      arr.forEach((m) => {
        if (m.path && m.menuKey)
          flat.push({
            path: m.path,
            menuKey: m.menuKey,
            alwaysShow: !!m.alwaysShow,
            masterOnly: !!m.masterOnly,
          });
        if (m.children) walk(m.children);
      });
    walk(fullMenuStructure);

    const path = location.pathname.replace(/\/+$/, '');
    // Match the most specific menu path the current URL falls under, so detail
    // routes like /admin/manageinventory/123 inherit their parent's grant.
    const match = flat
      .filter((e) => path === e.path || path.startsWith(e.path + '/'))
      .sort((a, b) => b.path.length - a.path.length)[0];

    if (!match || match.alwaysShow) return; // unmapped/always-on -> allow

    const planAllowed =
      !planAccess ||
      planAccess.allMenus ||
      (planAccess.menuKeys || []).includes(match.menuKey);

    // The page-wise "Open …" switch, same rule the sidebar applies below.
    const pageAllowed = hasStoredPermission(PAGE_RBAC[match.path]);

    if (match.masterOnly || !keys.has(match.menuKey) || !planAllowed || !pageAllowed) {
      navigate('/admin/welcome', { replace: true });
    }
  }, [location.pathname, planAccess]);

  const hasDashboardPermission = filteredMenu.some(
    (m) =>
      m.menuKey === 'dashboard' ||
      (m.children || []).some((c) => c.menuKey === 'dashboard'),
  );
  // The restaurant's own landing screen, asked the same way. Read off the
  // filtered menu rather than the raw grant, so the plan cap and the
  // property-type rules are already accounted for.
  const hasRestaurantDashboard = filteredMenu.some(
    (m) =>
      m.menuKey === 'resturantdashbord' ||
      (m.children || []).some((c) => c.menuKey === 'resturantdashbord'),
  );

  // The first screen this person actually has.
  //
  // Somebody without a dashboard used to land on /admin/welcome - a purple
  // gradient and a waving hand. For a waiter that is the whole of their first
  // impression: a greeting, and then they have to go and find the till
  // themselves. Their sidebar already knows what they can open; the first entry
  // in it is a better answer than a greeting.
  //
  // Read off filteredMenu, so it is already past the grant, the plan, the
  // property type and the page-wise switches - whatever comes back is a screen
  // that will actually open.
  const firstAvailablePath = useMemo(() => {
    for (const m of filteredMenu) {
      if (m.path) return m.path;
      const child = (m.children || []).find((c) => c.path);
      if (child) return child.path;
    }
    return null;
  }, [filteredMenu]);

  useEffect(() => {
    // Build the menuKey grant set (empty if the user has no permissions yet) —
    // master-admin items don't depend on it, so they still show for a master
    // even when the permission list is absent.
    let keys = new Set();
    const raw = sessionStorage.getItem('permissions');
    if (raw) {
      try {
        const permissions = JSON.parse(raw);
        keys = new Set(permissions.map((p) => p.menuKey));
      } catch {
        keys = new Set();
      }
    }

    // Plan cap: until plan menus load (null) don't restrict; trial/allMenus = no cap.
    const planAllowed = (k) =>
      !planAccess ||
      planAccess.allMenus ||
      (planAccess.menuKeys || []).includes(k);
    // menuKey grant AND plan AND the page-wise "Open …" switch. A path with
    // no entry in PAGE_RBAC has no switch and passes.
    const allowed = (c) =>
      c.alwaysShow ||
      (keys.has(c.menuKey) &&
        planAllowed(c.menuKey) &&
        hasStoredPermission(PAGE_RBAC[c.path]));
    // Apply the per-user menuKey + master-only visibility rules to a menu array.
    const filterMenus = (arr) =>
      arr
        .map((menu) => {
          // Dashboards are STRICTLY propertType-driven, independent of whether a
          // hotel also runs a restaurant: propertType 'H' shows ONLY the Hotel
          // Dashboard, propertType 'R' shows ONLY the Restaurant Dashboard. A
          // hotel that runs a restaurant switches views via the Hotel/Restaurant
          // toggle inside its dashboard, so the separate Restaurant Dashboard
          // menu must never appear for it.
          if (menu.text === 'Restaurant Dashboard' && !isRestaurantOnly)
            return null;
          if (menu.text === 'Hotel Dashboard' && isRestaurantOnly) return null;
          // #2: hide entire Hotel / Restaurant module groups per the wizard
          // selection (restaurant-only hides hotel groups, and vice-versa).
          if (RESTAURANT_MENUS.has(menu.text) && !restaurantEnabled)
            return null;
          if (HOTEL_MENUS.has(menu.text) && !hotelEnabled) return null;
          // Master-admin-only items (Company Management) bypass the per-user
          // menuKey grants — shown ONLY to a master admin, hidden from all
          // normal company users.
          if (menu.masterOnly) {
            return Users?.isMaster ? menu : null;
          }
          if (menu.children) {
            // Children obey the SAME domain + master-only rules as top level,
            // because departments now mix hotel and restaurant rows (My Day,
            // Guest Services, Reports, Masters).
            const visibleChildren = menu.children.filter((c) => {
              // Same strict propertType rule for dashboards if ever nested.
              if (c.text === 'Restaurant Dashboard' && !isRestaurantOnly)
                return false;
              if (c.text === 'Hotel Dashboard' && isRestaurantOnly)
                return false;
              if (RESTAURANT_MENUS.has(c.text) && !restaurantEnabled)
                return false;
              if (HOTEL_MENUS.has(c.text) && !hotelEnabled) return false;
              if (c.masterOnly) return !!Users?.isMaster;
              return allowed(c);
            });
            // Pages INSIDE each group are shown alphabetically (A→Z, case-
            // insensitive). The GROUP order itself (Reception, Accommodation, …)
            // is left untouched — only the children within a group are sorted.
            const sortedChildren = [...visibleChildren].sort((a, b) =>
              (a.text || '').localeCompare(b.text || '', undefined, { sensitivity: 'base' }));
            const parentAllowed = menu.menuKey
              ? keys.has(menu.menuKey) && planAllowed(menu.menuKey)
              : sortedChildren.length > 0;
            return parentAllowed
              ? { ...menu, children: sortedChildren }
              : null;
          }
          return allowed(menu) ? menu : null;
        })
        .filter(Boolean);

    // 1) Render the proven STATIC menu immediately (zero-regression baseline).
    setFilteredMenu(filterMenus(fullMenuStructure));

    // 2) Then ADDITIVELY merge any DB-defined menus (MenuMaster). Only menuKeys
    //    NOT already in the static menu are appended, so this can never break or
    //    duplicate existing navigation. If the fetch fails, the static menu stays.
    const company = JSON.parse(
      sessionStorage.getItem('companyDetails') || '{}',
    );
    const token = sessionStorage.getItem('token');
    if (!company?.id) return;
    fetch(`${config.API_BASE_URL}/menu/menus/${company.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((db) => {
        if (!Array.isArray(db) || db.length === 0) return;

        // Collect every menuKey already defined in the static structure.
        const staticKeys = new Set();
        const collect = (arr) =>
          arr.forEach((m) => {
            if (m.menuKey) staticKeys.add(m.menuKey);
            if (m.children) collect(m.children);
          });
        collect(fullMenuStructure);

        // Top-level DB menus (no parent) whose key isn't already static.
        const extra = db
          .filter((m) => !m.parentId)
          .filter((m) => m.menuKey && !staticKeys.has(m.menuKey))
          .map((parent) => {
            const node = {
              text: parent.text,
              icon: iconFor(parent.icon),
              path: parent.path,
              menuKey: parent.menuKey,
              masterOnly: parent.masterOnly,
            };
            const kids = db.filter((c) => c.parentId === parent.id);
            if (kids.length) {
              node.children = kids.map((k) => ({
                text: k.text,
                icon: iconFor(k.icon),
                path: k.path,
                menuKey: k.menuKey,
              }));
            }
            return node;
          });

        if (extra.length === 0) return;
        setFilteredMenu(filterMenus([...fullMenuStructure, ...extra]));
      })
      .catch(() => {});
  }, [planAccess]);

  useEffect(() => {
    const onCreateOrder = inRestaurantOrders(location.pathname);
    if (onCreateOrder) {
      if (!createOrderPinnedExpandedRef.current) {
        setDrawerOpen(false);
      }
      return;
    }
    createOrderPinnedExpandedRef.current = false;
    setDrawerOpen(!isMobile);
  }, [isMobile, location.pathname]);

  useEffect(() => {
    const next = {};
    filteredMenu.forEach((menu) => {
      if (menu.children) {
        next[menu.text] = menu.children.some((c) =>
          location.pathname.startsWith(c.path),
        );
      }
    });
    setOpenMenus(next);
  }, [location.pathname, filteredMenu]);

  const toggleDrawer = () => {
    setDrawerOpen((p) => {
      const next = !p;
      const onCreateOrder = inRestaurantOrders(location.pathname);
      if (onCreateOrder && next) {
        createOrderPinnedExpandedRef.current = true;
      }
      return next;
    });
  };
  const toggleMenu = (key) => setOpenMenus((p) => ({ ...p, [key]: !p[key] }));
  const isSelected = (path) => location.pathname === path;

  // ── One-time direct-login link ──
  // Mints a single-use URL: opening it logs straight in WITHOUT company code /
  // username / password. It works EXACTLY ONCE — a second hit cannot log in.
  const [directLinkBusy, setDirectLinkBusy] = React.useState(false);
  const handleDirectLoginLink = async () => {
    if (directLinkBusy) return;
    try {
      setDirectLinkBusy(true);
      const userId = Number(sessionStorage.getItem('userId'));
      const companyId = Number(sessionStorage.getItem('companyId'));
      const username = sessionStorage.getItem('username') || '';
      const client = localStorage.getItem('client') || '';
      const token = sessionStorage.getItem('token') || '';
      if (!userId || !companyId || !username) {
        alert('Could not read your session. Please re-login and try again.');
        return;
      }
      const res = await fetch(
        `${config.API_BASE_URL}/account/generate-direct-login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, companyId, username, client }),
        },
      );
      if (!res.ok) {
        alert('Could not generate a login link. Please try again.');
        return;
      }
      const data = await res.json();
      const url = `${window.location.origin}/direct-login?t=${encodeURIComponent(
        data.token,
      )}`;
      try {
        await navigator.clipboard.writeText(url);
        window.prompt(
          'One-time login link copied. It works only ONCE. Save it now:',
          url,
        );
      } catch {
        window.prompt(
          'One-time login link (works only ONCE). Copy it now:',
          url,
        );
      }
    } catch (e) {
      console.error('Direct-login link error:', e);
      alert('Could not generate a login link. Please try again.');
    } finally {
      setDirectLinkBusy(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('permissions');
      sessionStorage.removeItem('permissionCodes');
      sessionStorage.removeItem('rbacKnownCodes');
      notifyRbacUpdated();
      navigate('/login');
    }
  };

  const drawerWidth = drawerOpen ? drawerWidthExpanded : drawerWidthCollapsed;

  /* ── Drawer content ── */
  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: S.bg,
      }}>
      {/* ── Logo / Company area ── */}
      <Box
        sx={{
          px: drawerOpen ? '16px' : '8px',
          py: '18px',
          borderBottom: `1px solid ${S.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          minHeight: 72,
        }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            flexShrink: 0,
            background: 'linear-gradient(135deg,#C8102E,#E23A4E)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(200,16,46,0.28)',
            overflow: 'hidden',
          }}>
          <Box
            component='img'
            src={
              company?.logo?.startsWith('http')
                ? company.logo
                : `${config.API_BASE_URL}${company?.logo}`
            }
            alt='logo'
            onError={(e) => {
              e.target.style.display = 'none';
            }}
            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        </Box>
        {drawerOpen && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '13.5px',
                fontWeight: 700,
                color: '#ffffff',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                letterSpacing: '0.1px',
              }}>
              {company?.name || brandName}
            </Typography>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 500,
                color: '#94a3b8',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {brandSub}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Navigation ── */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          overflowY: subLocked ? 'hidden' : 'auto',
          overflowX: 'hidden',
          py: '8px',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.14)',
            borderRadius: 2,
          },
        }}>
        {/* Lock overlay — hides + disables the menu when subscription is invalid */}
        {subLocked && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 5,
              backdropFilter: 'blur(2px)',
              background: 'rgba(26,34,56,0.86)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.25,
              px: 2,
              textAlign: 'center',
              cursor: 'not-allowed',
            }}>
            <LockIcon sx={{ fontSize: 30, color: '#f59e0b' }} />
            {drawerOpen && (
              <>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 700, color: '#ffffff' }}>
                  {trialLapsed ? 'Free Trial Expired' : 'Subscription Required'}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: 'rgba(148,163,184,0.9)',
                    lineHeight: 1.4,
                  }}>
                  {trialLapsed
                    ? 'Please contact the Administrator to activate a plan.'
                    : 'Choose or renew a plan to unlock the menu.'}
                </Typography>
                {/* No upgrade button on a lapsed trial. Buying is not the way
                    back in, and offering it sends the user to a payment that
                    would not restore access on its own. */}
                {!trialLapsed && (
                  <Button
                    size='small'
                    variant='contained'
                    startIcon={<UpgradeIcon />}
                    onClick={() => {
                      navigate('/admin/Subscription');
                      if (isMobile) setDrawerOpen(false);
                    }}
                    sx={{
                      mt: 0.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      background: 'linear-gradient(135deg,#16a34a,#22c55e)',
                      '&:hover': {
                        background: 'linear-gradient(135deg,#15803d,#16a34a)',
                      },
                    }}>
                    Upgrade Now
                  </Button>
                )}
              </>
            )}
          </Box>
        )}
        <List
          disablePadding
          sx={{
            pointerEvents: subLocked ? 'none' : 'auto',
            opacity: subLocked ? 0.4 : 1,
          }}>
          {filteredMenu.map((menu) => {
            if (!menu.children) {
              return (
                <NavItem
                  key={menu.text}
                  menu={menu}
                  isActive={isSelected(menu.path)}
                  expanded={drawerOpen}
                  onClick={() => {
                    navigate(menu.path);
                    if (isMobile) setDrawerOpen(false);
                  }}
                />
              );
            }

            const isOpen = openMenus[menu.text] || false;
            const anyChildActive = menu.children.some((c) =>
              location.pathname.startsWith(c.path),
            );

            return (
              <React.Fragment key={menu.text}>
                <NavItem
                  menu={menu}
                  isOpen={isOpen}
                  isActive={anyChildActive && !isOpen}
                  expanded={drawerOpen}
                  onClick={() => toggleMenu(menu.text)}
                />
                <Collapse in={isOpen} timeout={200} unmountOnExit>
                  <List disablePadding>
                    {menu.children.map((child) => (
                      <ChildItem
                        key={child.text + child.path}
                        child={child}
                        isActive={isSelected(child.path)}
                        expanded={drawerOpen}
                        onClick={() => {
                          navigate(child.path);
                          if (isMobile) setDrawerOpen(false);
                        }}
                      />
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      {/* ── Bottom: User + Direct link + Upgrade + Logout + Brand ── */}
      <Box sx={{ borderTop: `1px solid ${S.border}`, pt: '8px', pb: '12px' }}>
        {/* Upgrade button — hidden per request */}
        {false && (
          <Tooltip
            title={drawerOpen ? '' : 'Upgrade Plan'}
            placement='right'
            arrow>
            <ListItemButton
              onClick={() => {
                navigate('/admin/Subscription');
                if (isMobile) setDrawerOpen(false);
              }}
              sx={{
                mx: '8px',
                borderRadius: '10px',
                mb: '8px',
                px: drawerOpen ? '12px' : '10px',
                py: '8px',
                justifyContent: drawerOpen ? 'initial' : 'center',
                color: '#86efac',
                background: 'rgba(34,197,94,0.12)',
                border: '1px solid rgba(34,197,94,0.25)',
                transition: 'all 0.15s',
                '&:hover': {
                  background: 'rgba(34,197,94,0.2)',
                  color: '#bbf7d0',
                },
              }}>
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: drawerOpen ? '10px' : 'auto',
                  justifyContent: 'center',
                  color: 'inherit',
                  '& svg': { fontSize: 18 },
                }}>
                <UpgradeIcon />
              </ListItemIcon>
              {drawerOpen && (
                <ListItemText
                  primary='Upgrade Plan'
                  primaryTypographyProps={{
                    fontSize: '13px',
                    fontWeight: 700,
                    fontFamily: 'Inter,sans-serif',
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        )}

        {/* Logout button */}
        <Tooltip title={drawerOpen ? '' : 'Logout'} placement='right' arrow>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              mx: '8px',
              borderRadius: '10px',
              mb: '8px',
              px: drawerOpen ? '12px' : '10px',
              py: '8px',
              justifyContent: drawerOpen ? 'initial' : 'center',
              color: '#f87171',
              transition: 'all 0.15s',
              '&:hover': {
                background: 'rgba(248,113,113,0.1)',
                color: '#fca5a5',
              },
            }}>
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: drawerOpen ? '10px' : 'auto',
                justifyContent: 'center',
                color: 'inherit',
                '& svg': { fontSize: 18 },
              }}>
              <ExitToAppIcon />
            </ListItemIcon>
            {drawerOpen && (
              <ListItemText
                primary='Logout'
                primaryTypographyProps={{
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: 'Inter,sans-serif',
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>

        {/* User info */}
        <Box
          sx={{
            mx: '8px',
            px: drawerOpen ? '12px' : '6px',
            py: '8px',
            borderRadius: '10px',
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${S.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            justifyContent: drawerOpen ? 'flex-start' : 'center',
          }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '8px',
              flexShrink: 0,
              background: 'linear-gradient(135deg,#C8102E,#9E0C22)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(200,16,46,0.28)',
            }}>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 800,
                color: '#fff',
                letterSpacing: '0.5px',
              }}>
              {userInitials}
            </Typography>
          </Box>
          {drawerOpen && (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'rgba(226,232,240,0.95)',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {userName}
              </Typography>
              <Typography
                sx={{
                  fontSize: '10px',
                  color: 'rgba(100,116,139,0.75)',
                  mt: '1px',
                }}>
                {userRole}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Powered by */}
        {drawerOpen && (
          <Box
            component='a'
            href='https://goerp.live'
            target='_blank'
            rel='noopener noreferrer'
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              mt: '10px',
              textDecoration: 'none',
              opacity: 0.45,
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 0.75 },
            }}>
            <Box
              component='img'
              src='/goerp.png'
              alt='GoERP'
              sx={{ height: 16, width: 'auto', objectFit: 'contain' }}
            />
            <Typography
              sx={{
                fontSize: '10px',
                color: 'rgba(148,163,184,0.8)',
                fontWeight: 500,
              }}>
              Powered by Go-ERP.Live
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  // Flattened, permission-filtered menu list for the header search. Reuses
  // filteredMenu (already RBAC + plan filtered), so the search only ever
  // surfaces menus this user is allowed to open.
  const menuSearchOptions = (() => {
    const out = [];
    const walk = (arr, dept) => {
      (arr || []).forEach((m) => {
        if (m.path)
          out.push({ label: m.text, path: m.path, group: dept || m.text });
        if (m.children) walk(m.children, m.text);
      });
    };
    walk(filteredMenu);

    // The Restaurant Orders tabs.
    //
    // They live inside that workspace, not in the sidebar, so walking the menu
    // never reached them - typing "Dine In", "QR Orders" or "Table Reservation"
    // found nothing at all, and the only way in was to know which tab strip
    // they were hiding on.
    //
    // Read from the strip's own list and its own permission filter, so search
    // can never offer a tab the strip would hide, and a tab added there turns
    // up here without anyone remembering to add it twice.
    //
    // Four of them share /admin/CreateOrder and differ only by ?type=, which is
    // what the POS reads to preset the order type - so the query has to travel
    // or every one of them lands on Dine In.
    try {
      allowedRestaurantTabs().forEach((t) => {
        out.push({
          label: t.label,
          path: t.type ? `${t.path}?type=${t.type}` : t.path,
          group: 'Restaurant Orders',
        });
      });
    } catch {
      /* never let the search box break over a menu list */
    }

    // The other three tab strips - Restaurant Setup, Restro Report, Pub / Club.
    //
    // Same problem as the Restaurant Orders tabs above: "Variants", "Area
    // Pricing" or "Z Report" live only inside their strip, so search found
    // nothing and the only way in was to know which strip hid them.
    //
    // A strip's tabs are offered only when the strip's own ENTRY POINT is in
    // this user's filtered sidebar. That carries every rule the sidebar already
    // applies - the domain (a hotel with no restaurant has no Restaurant Setup),
    // the plan and the grants - without repeating any of them here. Within a
    // strip, its own permission filter then decides which tabs.
    const sidebarPaths = new Set();
    const collect = (arr) =>
      (arr || []).forEach((m) => {
        if (m.path) sidebarPaths.add(m.path);
        if (m.children) collect(m.children);
      });
    collect(filteredMenu);

    const TAB_STRIPS = [
      { entry: '/admin/AddItem', group: 'Restaurant Setup', tabs: allowedRestaurantSetupTabs },
      { entry: '/admin/RestaurantInvoiceReport', group: 'Restro Report', tabs: allowedRestroReportTabs },
      { entry: '/admin/PubCardRequests', group: 'Pub / Club', tabs: allowedPubCardTabs },
    ];
    TAB_STRIPS.forEach((strip) => {
      if (!sidebarPaths.has(strip.entry)) return;
      try {
        strip.tabs().forEach((t) => {
          // The sidebar may already list the same screen under the same name
          // (Pub / Club → Card Requests). Offered twice it reads as two places.
          const dup = out.some(
            (o) =>
              o.path === t.path &&
              (o.label || '').toLowerCase() === (t.label || '').toLowerCase(),
          );
          if (!dup) out.push({ label: t.label, path: t.path, group: strip.group });
        });
      } catch {
        /* never let the search box break over a menu list */
      }
    });

    // Autocomplete draws a heading wherever the group CHANGES, so a group split
    // across the list (the sidebar's Pub / Club, then its tabs further down)
    // gets two headings. Keep each group together, in the order it first
    // appeared - Array.sort is stable, so the order inside a group holds.
    const firstSeen = new Map();
    out.forEach((o, i) => {
      if (!firstSeen.has(o.group)) firstSeen.set(o.group, i);
    });
    out.sort((a, b) => firstSeen.get(a.group) - firstSeen.get(b.group));

    return out;
  })();

  return (
    <Box sx={{ display: 'flex', bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <CssBaseline />

      {/* ── TOP APP BAR ── */}
      <AppBar
        position='fixed'
        elevation={0}
        sx={{
          // Gone while the POS is in full screen.
          display: posFullscreen ? 'none' : 'flex',
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: '#fff',
          borderBottom: '1px solid rgba(226,232,240,0.8)',
          boxShadow: '0 1px 8px rgba(15,23,42,0.06)',
          transition: 'width 0.25s ease, margin 0.25s ease',
          zIndex: theme.zIndex.drawer + 1,
        }}>
        {/* 80px of header is a quarter of a phone screen spent on chrome. 56
            is the height a mobile app bar actually uses; desktop keeps its 80.
            The main content's top margin below matches this, or the first row
            of every page hides underneath. */}
        <Toolbar
          sx={{
            // Desktop trimmed 80 -> 64: the bar holds a search box and five
            // small controls, and the 16px it gives back are 16px of every
            // page below it. The main content's top margin matches.
            minHeight: { xs: '56px !important', sm: '64px !important' },
            px: { xs: '8px', sm: '20px' },
            gap: { xs: 0.5, sm: 0 },
          }}>
          <IconButton
            onClick={toggleDrawer}
            sx={{
              mr: '14px',
              color: '#475569',
              width: 36,
              height: 36,
              borderRadius: '10px',
              border: '1px solid rgba(226,232,240,0.8)',
              '&:hover': { bgcolor: '#F1F5F9', color: '#1E293B' },
              transition: 'all 0.15s',
            }}>
            {drawerOpen ? (
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            ) : (
              <MenuIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>

          {/* #3: Back button on EVERY page (this AppBar wraps all admin pages via
              <Outlet/>). Hidden on the home/landing pages so "Back" never leaves
              the admin area. navigate(-1) = browser-style previous page. */}
          {location.pathname !== '/admin' &&
            location.pathname !== '/admin/dashboard' &&
            location.pathname !== '/admin/resturantdashbord' &&
            location.pathname !== '/admin/welcome' && (
              <Tooltip title='Back' arrow>
                <IconButton
                  onClick={() => navigate(-1)}
                  sx={{
                    mr: '12px',
                    color: '#475569',
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    border: '1px solid rgba(226,232,240,0.8)',
                    '&:hover': { bgcolor: '#F1F5F9', color: '#1E293B' },
                    transition: 'all 0.15s',
                  }}>
                  <ArrowBackIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}

          {/* Title — brand LOGO (hotel vs restaurant) + subtitle. The logo
              image replaces the old "Hotel Desk" / "Dine In Master" wordmark;
              subtitle text (Hotel Management / Restro Management) stays. */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: '6px', sm: '10px' },
            }}>
            <Box
              component='img'
              src={APP_BRAND.logo}
              alt={brandName}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
              sx={{
                // 70px of logo does not fit a 56px phone bar, and it was
                // taking the width the search box needed. Half size on a
                // phone; on desktop it now follows the 64px bar (it used to
                // be 70px - taller than the bar itself).
                // The search is an icon on phones now, so the logo can have
                // some of that space back - but a 360px phone and a 430px one
                // do not have the same to give, so it grows in three steps
                // and only within phone widths (from sm the desktop size
                // takes over).
                height: { xs: 28, sm: 46 },
                width: 'auto',
                maxWidth: { xs: 86, sm: 200 },
                '@media (min-width:390px) and (max-width:599.95px)': {
                  height: 34,
                  maxWidth: 112,
                },
                '@media (min-width:430px) and (max-width:599.95px)': {
                  height: 38,
                  maxWidth: 132,
                },
                objectFit: 'contain',
                // Steps aside while the phone's search field is open.
                display: { xs: topSearchOpen ? 'none' : 'block', sm: 'block' },
                flexShrink: 0,
              }}
            />
            <Autocomplete
              // Records first, menus underneath. Grouping keeps both useful:
              // "Payments" the screen and "Amit Verma" the guest can coexist.
              options={[
                ...searchHits.map((h) => ({ ...h, group: h.kind, isRecord: true })),
                ...menuSearchOptions,
              ]}
              filterOptions={(opts, state) =>
                // Records are already filtered by the server; only the menu
                // options need client-side matching.
                opts.filter(
                  (o) =>
                    o.isRecord ||
                    (o.label || '')
                      .toLowerCase()
                      .includes((state.inputValue || '').toLowerCase()),
                )
              }
              groupBy={(o) => o.group}
              getOptionLabel={(o) => (o.isRecord ? o.title || '' : o.label || '')}
              isOptionEqualToValue={(o, v) =>
                o.isRecord ? o.kind === v.kind && o.id === v.id : o.path === v.path
              }
              openOnFocus
              autoHighlight
              blurOnSelect
              clearOnBlur
              value={null}
              inputValue={searchText}
              onInputChange={(e, v, reason) => {
                if (reason !== 'reset') setSearchText(v);
              }}
              loading={searchBusy}
              onChange={(e, val) => {
                if (!val) return;
                setSearchText('');
                if (val.isRecord) {
                  if (val.route) navigate(val.route);
                } else if (val.path) {
                  navigate(val.path);
                }
              }}
              renderOption={(props, o) => {
                const { key, ...rest } = props;
                if (!o.isRecord) {
                  return (
                    <li key={key} {...rest}>
                      <Typography sx={{ fontSize: 13.5 }}>{o.label}</Typography>
                    </li>
                  );
                }
                const tone =
                  { success: '#15803D', warning: '#B45309', error: '#B91C1C', info: '#0369A1' }[
                    o.badgeTone
                  ] || '#475569';
                return (
                  <li key={key} {...rest}>
                    <Box sx={{ width: '100%', minWidth: 0, py: 0.2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography
                          sx={{ fontSize: 13.5, fontWeight: 700, color: '#1A2238', flex: 1 }}
                          noWrap>
                          {o.title}
                        </Typography>
                        {o.badge && (
                          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: tone }}>
                            {o.badge}
                          </Typography>
                        )}
                      </Box>
                      {o.subtitle && (
                        <Typography sx={{ fontSize: 11.5, color: '#64748B' }} noWrap>
                          {o.subtitle}
                        </Typography>
                      )}
                      {o.detail && (
                        <Typography sx={{ fontSize: 11, color: '#94A3B8' }} noWrap>
                          {o.detail}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              size='small'
              sx={{
                // Phone: take whatever is left after the logo instead of a
                // fixed 160px. With a fixed width the bar added up to more
                // than the screen, so the speaker and the bell were sitting
                // on top of the search box.
                width: { xs: 'auto', sm: 280, md: 360 },
                flex: { xs: 1, sm: '0 0 auto' },
                minWidth: 0,
                mx: { xs: 0, sm: 'auto' },
                // Phone: hidden until the search icon is pressed.
                display: { xs: topSearchOpen ? 'flex' : 'none', sm: 'flex' },
              }}
              ListboxProps={{ style: { maxHeight: 360 } }}
              noOptionsText={
                searchText.trim().length < 2
                  ? 'Type a guest, mobile, booking, room or invoice'
                  : searchBusy
                    ? 'Searching…'
                    : 'Nothing found'
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={topSearchInputRef}
                  placeholder='Search guest, booking, room…'
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <span
                        className='material-symbols-rounded'
                        style={{
                          fontSize: 18,
                          color: '#94a3b8',
                          marginLeft: 6,
                          marginRight: 6,
                        }}>
                        search
                      </span>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '22px',
                      bgcolor: '#f8fafc',
                      fontSize: '0.85rem',
                    },
                  }}
                />
              )}
            />

            {/* Phone only. Closed: a spacer pushes the magnifier over to the
                speaker, so the bar reads logo … icons. Open: the field takes
                the row and this X gives it back. */}
            <Box
              sx={{
                display: { xs: topSearchOpen ? 'none' : 'block', sm: 'none' },
                flex: 1,
              }}
            />
            <IconButton
              aria-label='Search'
              size='small'
              onClick={() => setTopSearchOpen(true)}
              sx={{
                display: { xs: topSearchOpen ? 'none' : 'inline-flex', sm: 'none' },
                color: '#64748b',
                flexShrink: 0,
              }}>
              <span className='material-symbols-rounded' style={{ fontSize: 22 }}>
                search
              </span>
            </IconButton>
            <IconButton
              aria-label='Close search'
              size='small'
              onClick={() => {
                setTopSearchOpen(false);
                // A hidden query still filtering nothing is confusing; the
                // next open starts clean.
                setSearchText('');
              }}
              sx={{
                display: { xs: topSearchOpen ? 'inline-flex' : 'none', sm: 'none' },
                color: '#64748b',
                flexShrink: 0,
              }}>
              <span className='material-symbols-rounded' style={{ fontSize: 22 }}>
                close
              </span>
            </IconButton>
          </Box>

          {/* Right side: live badge + user pill */}
          <Box
            sx={{
              // While the phone's search field is open it owns the bar; the
              // speaker, the bell and the avatar come back on close.
              display: { xs: topSearchOpen ? 'none' : 'flex', sm: 'flex' },
              alignItems: 'center',
              // Tight on a phone: these controls and the search have to share
              // 412px.
              gap: { xs: '2px', sm: '10px' },
              flexShrink: 0,
            }}>
            {/* Speaker: mutes the bell's chime + voice on THIS device only.
                What it says, and in which language, is company-wide under
                Setup → Business Settings → Sound Settings. */}
            <Tooltip
              title={
                notifSound.muted
                  ? 'Notification sound is off on this device'
                  : 'Notification sound is on for this device'
              }>
              <IconButton
                size='small'
                sx={{ color: notifSound.muted ? '#DC2626' : '#64748b', flexShrink: 0 }}
                onClick={() => notifSound.setMuted(!notifSound.muted)}>
                <span
                  className='material-symbols-rounded'
                  style={{
                    fontSize: 22,
                    fontVariationSettings:
                      "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24",
                  }}>
                  {notifSound.muted ? 'volume_off' : 'volume_up'}
                </span>
              </IconButton>
            </Tooltip>
            {/* Notification bell — Inventory Alerts (batch expiry). */}
            <IconButton
              size='small'
              sx={{ color: '#64748b', flexShrink: 0 }}
              onClick={(e) => setBellAnchor(e.currentTarget)}>
              <Badge
                badgeContent={
                  qrPending.length +
                  invoiceReqs.length +
                  appOrders.length +
                  invTotal +
                  otaPending.total
                }
                color='error'
                max={99}>
                <span
                  className='material-symbols-rounded'
                  style={{
                    fontSize: 22,
                    fontVariationSettings:
                      "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24",
                  }}>
                  notifications
                </span>
              </Badge>
            </IconButton>
            <Popover
              open={Boolean(bellAnchor)}
              anchorEl={bellAnchor}
              onClose={() => setBellAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  width: 360,
                  maxWidth: '92vw',
                  borderRadius: '14px',
                  mt: 1,
                },
              }}>
              {/* OTA bookings first: an unimported booking can still be sold to
                  someone else, so it is more urgent than an expiring batch. */}
              {otaPending.total > 0 && (
                <Box sx={{ borderBottom: '1px solid #E7ECF3' }}>
                  <Box
                    sx={{
                      p: 1.6,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setBellAnchor(null);
                      navigate('/admin/ChannelManager');
                    }}>
                    <span
                      className='material-symbols-rounded'
                      style={{ fontSize: 20, color: '#0B6E99' }}>
                      travel_explore
                    </span>
                    <Typography sx={{ fontWeight: 800, color: '#1A2238', flex: 1 }}>
                      OTA Bookings
                    </Typography>
                    <Box
                      sx={{
                        px: 1, py: 0.2, borderRadius: 999,
                        bgcolor: '#0B6E99', color: '#fff',
                        fontSize: 12, fontWeight: 800,
                      }}>
                      {otaPending.total}
                    </Box>
                  </Box>

                  {otaPending.recent.map((b) => (
                    <Box
                      key={b.id}
                      onClick={() => {
                        setBellAnchor(null);
                        navigate('/admin/ChannelManager');
                      }}
                      sx={{
                        px: 1.6, py: 0.9, cursor: 'pointer',
                        borderTop: '1px solid #F1F5F9',
                        '&:hover': { bgcolor: '#F8FAFC' },
                      }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1A2238' }} noWrap>
                        {b.guestName || 'Guest'}
                        {b.channelName ? ` · ${b.channelName}` : ''}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: '#64748B' }} noWrap>
                        {[b.checkIn, b.checkOut].filter(Boolean).join(' → ') || 'Dates not read'}
                        {b.amount ? ` · ₹${b.amount}` : ''}
                      </Typography>
                      {/* Anything the desk must decide before importing. */}
                      {b.errorMessage && (
                        <Typography sx={{ fontSize: 11, color: '#B45309' }} noWrap>
                          {b.errorMessage}
                        </Typography>
                      )}
                    </Box>
                  ))}

                  <Box
                    onClick={() => {
                      setBellAnchor(null);
                      navigate('/admin/ChannelManager');
                    }}
                    sx={{
                      px: 1.6, py: 0.9, cursor: 'pointer',
                      borderTop: '1px solid #F1F5F9',
                      fontSize: 12, fontWeight: 700, color: '#0B6E99',
                    }}>
                    Open Channel Manager →
                  </Box>
                </Box>
              )}

              {/* ── QR orders waiting for approval ── */}
              {qrPending.length > 0 && (
                <Box sx={{ borderBottom: '1px solid #E7ECF3' }}>
                  <Box sx={{ p: 1.6, pb: 0.8, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span className='material-symbols-rounded'
                          style={{ fontSize: 20, color: '#C8102E' }}>qr_code_2</span>
                    <Typography sx={{ fontWeight: 800, color: '#1A2238', flexGrow: 1 }}>
                      QR Orders
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#C8102E' }}>
                      {qrPending.length}
                    </Typography>
                  </Box>
                  {/* Only the first few. A bell is a prompt to go and look,
                      not the screen you work from. */}
                  {qrPending.slice(0, 4).map((o) => (
                    <Box
                      key={`${o.orderId}-${o.batchId || 0}`}
                      onClick={() => { setBellAnchor(null); navigate('/admin/QrOrders'); }}
                      sx={{
                        px: 1.6, py: 0.9, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 1,
                        '&:hover': { bgcolor: '#F6F8FB' },
                      }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, flexGrow: 1 }}>
                        {o.isRoom && o.roomNo ? `Room ${o.roomNo}` : o.tableNo || 'Table'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>
                        {o.amount != null ? `₹${Number(o.amount).toFixed(0)}` : ''}
                      </Typography>
                    </Box>
                  ))}
                  <Box
                    onClick={() => { setBellAnchor(null); navigate('/admin/QrOrders'); }}
                    sx={{
                      px: 1.6, py: 1, cursor: 'pointer',
                      fontSize: '0.78rem', fontWeight: 700, color: '#C8102E',
                      '&:hover': { bgcolor: '#F6F8FB' },
                    }}>
                    Approve orders →
                  </Box>
                </Box>
              )}

              {/* ── Orders placed from the waiter app ── */}
              {appOrders.length > 0 && (
                <Box sx={{ borderBottom: '1px solid #E7ECF3' }}>
                  <Box sx={{ p: 1.6, pb: 0.8, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span className='material-symbols-rounded'
                          style={{ fontSize: 20, color: '#1D4ED8' }}>smartphone</span>
                    <Typography sx={{ fontWeight: 800, color: '#1A2238', flexGrow: 1 }}>
                      App Orders
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#1D4ED8' }}>
                      {appOrders.length}
                    </Typography>
                  </Box>
                  {appOrders.slice(0, 4).map((o) => (
                    <Box
                      key={o.orderId}
                      onClick={() => openAppOrder(o)}
                      sx={{
                        px: 1.6, py: 0.9, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 1,
                        '&:hover': { bgcolor: '#F6F8FB' },
                      }}>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }} noWrap>
                          {o.isRoom
                            ? `Room ${o.roomNo || ''}`.trim()
                            : o.tableNo || 'Order'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
                          Order #{o.orderId} · from app
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                        {o.at
                          ? new Date(o.at).toLocaleTimeString('en-IN', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : ''}
                      </Typography>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      onClick={() => { setBellAnchor(null); navigate('/admin/TableOrders?type=DineIn'); }}
                      sx={{
                        px: 1.6, py: 1, cursor: 'pointer', flexGrow: 1,
                        fontSize: '0.78rem', fontWeight: 700, color: '#1D4ED8',
                        '&:hover': { bgcolor: '#F6F8FB' },
                      }}>
                      Open table orders →
                    </Box>
                    <Box
                      onClick={clearAppOrders}
                      sx={{
                        px: 1.6, py: 1, cursor: 'pointer',
                        fontSize: '0.78rem', fontWeight: 700, color: '#64748b',
                        '&:hover': { bgcolor: '#F6F8FB' },
                      }}>
                      Clear
                    </Box>
                  </Box>
                </Box>
              )}

              {/* ── Invoice requests from the waiter app ── */}
              {invoiceReqs.length > 0 && (
                <Box sx={{ borderBottom: '1px solid #E7ECF3' }}>
                  <Box sx={{ p: 1.6, pb: 0.8, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span className='material-symbols-rounded'
                          style={{ fontSize: 20, color: '#C8102E' }}>receipt_long</span>
                    <Typography sx={{ fontWeight: 800, color: '#1A2238', flexGrow: 1 }}>
                      Invoice Requests
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#C8102E' }}>
                      {invoiceReqs.length}
                    </Typography>
                  </Box>
                  {invoiceReqs.slice(0, 4).map((r) => (
                    <Box
                      key={r.id}
                      onClick={() => openInvoiceRequest(r)}
                      sx={{
                        px: 1.6, py: 0.9, cursor: 'pointer',
                        '&:hover': { bgcolor: '#F6F8FB' },
                      }}>
                      <Typography sx={{ fontSize: '0.82rem' }}>
                        {r.message || `Invoice requested for order #${r.orderId}`}
                      </Typography>
                    </Box>
                  ))}
                  <Box
                    onClick={() => { setBellAnchor(null); navigate('/admin/TableOrders'); }}
                    sx={{
                      px: 1.6, py: 1, cursor: 'pointer',
                      fontSize: '0.78rem', fontWeight: 700, color: '#C8102E',
                      '&:hover': { bgcolor: '#F6F8FB' },
                    }}>
                    Open table orders →
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  p: 1.6,
                  borderBottom: '1px solid #E7ECF3',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                <span
                  className='material-symbols-rounded'
                  style={{ fontSize: 20, color: '#C8102E' }}>
                  inventory_2
                </span>
                <Typography sx={{ fontWeight: 800, color: '#1A2238' }}>
                  Inventory Alerts
                </Typography>
              </Box>
              {/* Summary chips → open the report filtered */}
              <Box sx={{ p: 1.2, display: 'flex', gap: 0.7, flexWrap: 'wrap' }}>
                {[
                  {
                    k: 'expired',
                    label: 'Expired',
                    c: '#DC2626',
                    n: invAlerts.summary?.expired || 0,
                  },
                  {
                    k: 'today',
                    label: 'Today',
                    c: '#DC2626',
                    n: invAlerts.summary?.today || 0,
                  },
                  {
                    k: 'next7',
                    label: '7 Days',
                    c: '#EA580C',
                    n: invAlerts.summary?.next7 || 0,
                  },
                  {
                    k: 'next30',
                    label: '30 Days',
                    c: '#CA8A04',
                    n: invAlerts.summary?.next30 || 0,
                  },
                ].map((b) => (
                  <Box
                    key={b.k}
                    onClick={() => openExpiry(b.k)}
                    sx={{
                      cursor: 'pointer',
                      px: 1,
                      py: 0.5,
                      borderRadius: '9px',
                      border: `1px solid ${b.c}33`,
                      background: `${b.c}0F`,
                      flex: 1,
                      textAlign: 'center',
                      minWidth: 64,
                    }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: b.c,
                        fontSize: 16,
                        lineHeight: 1,
                      }}>
                      {b.n}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        color: '#64748b',
                        fontWeight: 600,
                      }}>
                      {b.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Divider />
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {(invAlerts.alerts || []).length === 0 ? (
                  <Typography
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      color: '#64748b',
                      fontSize: 13,
                    }}>
                    No inventory expiring soon. 🎉
                  </Typography>
                ) : (
                  (invAlerts.alerts || []).map((a) => {
                    const isOpen = expandedAlert === a.batchId;
                    return (
                      <Box
                        key={a.batchId}
                        sx={{ borderBottom: '1px solid #F1F5F9' }}>
                        <Box
                          onClick={() =>
                            setExpandedAlert(isOpen ? null : a.batchId)
                          }
                          sx={{
                            px: 1.6,
                            py: 1,
                            cursor: 'pointer',
                            display: 'flex',
                            gap: 1,
                            alignItems: 'flex-start',
                            '&:hover': { background: '#F8FAFC' },
                          }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              mt: 0.7,
                              background: alertColor(a.status),
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: '#1A2238',
                                fontSize: 13.5,
                              }}
                              noWrap>
                              {a.itemName || 'Item'}
                            </Typography>
                            <Typography
                              sx={{ fontSize: 11.5, color: '#64748b' }}
                              noWrap>
                              {a.batchNumber ? `Batch ${a.batchNumber} · ` : ''}
                              {a.qtyAvailable} {a.store ? `· ${a.store}` : ''}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                color: alertColor(a.status),
                                whiteSpace: 'nowrap',
                              }}>
                              {alertDays(a.daysRemaining)}
                            </Typography>
                            <span
                              className='material-symbols-rounded'
                              style={{ fontSize: 16, color: '#94a3b8' }}>
                              {isOpen ? 'expand_less' : 'expand_more'}
                            </span>
                          </Box>
                        </Box>
                        {isOpen && (
                          <Box
                            sx={{
                              px: 1.6,
                              pb: 1.2,
                              display: 'flex',
                              gap: 0.7,
                              flexWrap: 'wrap',
                            }}>
                            {alertActions(a).map((act) => (
                              <Box
                                key={act.label}
                                onClick={act.onClick}
                                sx={{
                                  cursor: invBusy ? 'wait' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  px: 1,
                                  py: 0.6,
                                  borderRadius: '8px',
                                  border: `1px solid ${act.danger ? '#DC262633' : '#E7ECF3'}`,
                                  background: act.danger
                                    ? '#FEF2F2'
                                    : '#F8FAFC',
                                  color: act.danger ? '#DC2626' : '#1A2238',
                                  fontSize: 11.5,
                                  fontWeight: 700,
                                  '&:hover': { filter: 'brightness(0.97)' },
                                }}>
                                <span
                                  className='material-symbols-rounded'
                                  style={{ fontSize: 15 }}>
                                  {act.icon}
                                </span>
                                {act.label}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>
              <Box
                onClick={() => openExpiry(null)}
                sx={{
                  p: 1.2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  color: '#C8102E',
                  fontWeight: 700,
                  fontSize: 13,
                  borderTop: '1px solid #E7ECF3',
                  '&:hover': { background: '#FEF2F2' },
                }}>
                View full Expiry Report →
              </Box>
            </Popover>
            {/* Live indicator */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: '5px',
                bgcolor: alpha('#10b981', 0.1),
                border: `1px solid ${alpha('#10b981', 0.25)}`,
                borderRadius: '20px',
                px: '10px',
                py: '4px',
              }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: '#10b981',
                  animation: 'apulse 2s infinite',
                  '@keyframes apulse': {
                    '0%,100%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.4, transform: 'scale(0.8)' },
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  color: '#10b981',
                  letterSpacing: '0.3px',
                }}>
                LIVE
              </Typography>
            </Box>

            {/* User pill */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                bgcolor: '#F8FAFC',
                border: '1px solid #E2E8F0',
                borderRadius: '10px',
                // On a phone this pill is only the initials square, so it
                // needs no side padding to sit in.
                px: { xs: '4px', md: '10px' },
                py: '5px',
                cursor: 'default',
              }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '7px',
                  flexShrink: 0,
                  background: 'linear-gradient(135deg,#C8102E,#9E0C22)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <Typography
                  sx={{ fontSize: '10px', fontWeight: 800, color: '#fff' }}>
                  {userInitials}
                </Typography>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#1E293B',
                    lineHeight: 1.1,
                  }}>
                  {userName}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>
                  {userRole}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── DRAWER ── */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={drawerOpen}
        onClose={toggleDrawer}
        ModalProps={{ keepMounted: true }}
        sx={{
          // Gone while the POS is in full screen (keepMounted above means the
          // menu itself is only hidden, never rebuilt).
          display: posFullscreen ? 'none' : 'block',
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: S.bg,
            borderRight: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '4px 0 24px rgba(0,0,0,0.28)',
            transition: 'width 0.25s ease',
            overflowX: 'hidden',
          },
        }}>
        {drawerContent}
      </Drawer>

      {/* ── MAIN CONTENT ── */}
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          // WITHOUT THIS THE WHOLE APP OVERFLOWS SIDEWAYS ON A PHONE.
          //
          // The root above is display:flex, so this is a flex item, and a flex
          // item's default is min-width:auto - it refuses to shrink below the
          // intrinsic width of its content. One wide thing anywhere inside (a
          // tab strip that does not wrap, a table with a minWidth, a nowrap
          // row) therefore pushes <main> wider than the screen, and the page
          // renders wider than the phone instead of fitting it.
          //
          // min-width:0 lets it shrink to the viewport. Inner containers that
          // genuinely need width keep their own overflowX:auto and scroll
          // inside themselves, which is what they were always meant to do.
          minWidth: 0,
          // Must track the Toolbar height above. The bar is fixed, so this
          // margin is the only thing keeping the first row of every page out
          // from under it.
          // In POS full screen the bar and the sidebar are gone, so the space
          // they were being kept clear of belongs to the till.
          mt: posFullscreen ? 0 : { xs: '56px', sm: '64px' },
          minHeight: posFullscreen
            ? '100vh'
            : { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
          width: posFullscreen ? '100%' : { sm: `calc(100% - ${drawerWidth}px)` },
          transition: 'width 0.25s ease',
          bgcolor: '#F8FAFC',
          p: 0,
        }}>
        {/* {location.pathname === '/admin' ? (
          hasDashboardPermission ? (
            <Navigate to='/admin/dashboard' replace />
          ) : (
            <Navigate to='/admin/welcome' replace />
          )
        ) : (
          <Outlet />
        )} */}
        {/* Where /admin lands.
            The restaurant branch used to go straight to its dashboard without
            asking whether this person may see one, so a waiter signing in was
            put on the day's takings. Both branches check first now, and anyone
            without a dashboard starts on welcome - which is what it is for. */}
        {location.pathname === '/admin' ? (
          // The sidebar is built in an effect, so on the very first render it
          // is still empty. Deciding then would send everybody to the fallback
          // before their own menu had been worked out - so wait one beat and
          // show the outline, exactly as a loading screen does.
          filteredMenu.length === 0 ? (
            <RouteSkeleton />
          ) : isRestaurantOnly ? (
            hasRestaurantDashboard ? (
              <Navigate to='/admin/resturantdashbord' replace />
            ) : firstAvailablePath ? (
              <Navigate to={firstAvailablePath} replace />
            ) : (
              <Navigate to='/admin/welcome' replace />
            )
          ) : hasDashboardPermission ? (
            <Navigate to='/admin/dashboard' replace />
          ) : firstAvailablePath ? (
            <Navigate to={firstAvailablePath} replace />
          ) : (
            // Nothing at all is granted. The greeting is the honest answer
            // here - there is no screen to send them to.
            <Navigate to='/admin/welcome' replace />
          )
        ) : (
          // The waiting happens HERE, inside the shell.
          //
          // Every screen is a separate chunk now, so opening one it has not
          // fetched yet makes React suspend. The only Suspense boundary was in
          // App.js, wrapped around the whole <Routes> - outside this layout -
          // so suspending tore down the entire application: sidebar, top bar
          // and all, replaced by a bare skeleton on a white page, and then
          // rebuilt a moment later. It looked like the app had crashed and
          // come back, and it happened on every first visit to a screen.
          //
          // With the boundary here the sidebar and the bar stay put and only
          // the content area shows the outline, which is what a skeleton is
          // for: the shape of the thing that is coming, in its place.
          <Suspense fallback={<RouteSkeleton />}>
            <Outlet />
          </Suspense>
        )}
      </Box>
    </Box>
  );
}
